import React, { useState, useEffect, useRef } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { 
  Brain, 
  Send, 
  MessageSquare, 
  Sparkles, 
  X, 
  Minimize2,
  Maximize2,
  Bot,
  User,
  Loader2,
  Monitor,
  Smartphone,
  Calendar,
  TrendingUp,
  Target,
  Users,
  Clock,
  AlertCircle,
  CheckCircle,
  Lightbulb,
  Zap,
  BarChart3,
  Settings,
  Plus,
  Edit,
  Trash2,
  Eye,
  Download,
  Upload
} from 'lucide-react'
import { cn } from '@utils/cn'
import { useAuth } from '@/contexts/AuthContext'
import { isFreePlanUser } from '@/utils/subscriptionUtils'
import SubscriptionUpgradeModal from '@/components/UI/SubscriptionUpgradeModal'
import AIService from '@utils/AIService'
import AIAPIService from '@utils/AIAPIService'
import ContentGeneratorService from '@/utils/ContentGeneratorService.js'

const AIAgentPanel = () => {
  const { currentUserData } = useAuth()
  const [showUpgradeModal, setShowUpgradeModal] = useState(false)
  const [isExpanded, setIsExpanded] = useState(true)
  const [messages, setMessages] = useState([
    {
      id: 1,
      type: 'ai',
      content: 'Hello! I\'m your AI assistant. I can help you with content creation, optimization, and managing your digital signage. What would you like to work on today?',
      timestamp: new Date(),
      actions: []
    }
  ])
  const [inputValue, setInputValue] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [systemData, setSystemData] = useState({
    devices: [],
    screens: [],
    schedules: [],
    analytics: {}
  })
  const [context, setContext] = useState('dashboard')
  const [aiService, setAiService] = useState(null)
  const [apiService, setApiService] = useState(null)
  const [contentGenerator, setContentGenerator] = useState(null)
  
  const navigate = useNavigate()
  const location = useLocation()
  const { currentUser } = useAuth()
  const messagesEndRef = useRef(null)

  // Update context based on current page
  useEffect(() => {
    const path = location.pathname
    if (path.includes('/screens')) setContext('screens')
    else if (path.includes('/devices')) setContext('devices')
    else if (path.includes('/channels')) setContext('channels')
    else if (path.includes('/schedule')) setContext('schedule')
    else if (path.includes('/analytics')) setContext('analytics')
    else if (path.includes('/ai-studio')) setContext('ai-studio')
    else setContext('dashboard')
  }, [location])

  // Initialize AI service and fetch system data
  useEffect(() => {
    const initializeAI = async () => {
      if (!currentUser?.uid) return
      
      try {
        const service = new AIService()
        const data = await service.initialize(currentUser.uid)
        
        setAiService(service)
        setSystemData(data)
        
        // Initialize API service
        const apiServiceInstance = new AIAPIService()
        setApiService(apiServiceInstance)
        
        // Initialize content generator service
        const contentGeneratorInstance = new ContentGeneratorService()
        setContentGenerator(contentGeneratorInstance)
      } catch (error) {
        console.error('Error initializing AI service:', error)
        // Set default data even if Firebase fails
        setSystemData({
          devices: [],
          screens: [],
          schedules: [],
          channels: [],
          analytics: {
            totalDevices: 0,
            onlineDevices: 0,
            offlineDevices: 0,
            totalScreens: 0,
            totalChannels: 0,
            activeChannels: 0,
            mutedChannels: 0,
            devicesUsingChannels: 0,
            devicesUsingScreens: 0,
            totalSchedules: 0,
            repetitiveSchedules: 0,
            regularSchedules: 0,
            onlinePercentage: 0,
            channelUtilization: 0,
            screenUtilization: 0,
            avgSchedulesPerDevice: 0,
            avgRepetitivePerDevice: 0,
            avgRegularPerDevice: 0,
            screenTypes: {},
            deviceHealth: [],
            performanceScore: 0
          }
        })
        
        // Still initialize other services
        const apiServiceInstance = new AIAPIService()
        setApiService(apiServiceInstance)
        
        const contentGeneratorInstance = new ContentGeneratorService()
        setContentGenerator(contentGeneratorInstance)
      }
    }

    initializeAI()
  }, [currentUser?.uid])

  // Refresh system data function
  const refreshSystemData = async () => {
    if (!aiService || !currentUser?.uid) return
    
    try {
      const refreshedData = await aiService.initialize(currentUser.uid)
      setSystemData(refreshedData)
    } catch (error) {
      console.error('Error refreshing system data:', error)
    }
  }

  // Refresh current page content based on what was deleted
  const refreshCurrentPage = async (deletedType) => {
    const currentPath = location.pathname
    
    try {
      // Refresh the appropriate data based on what was deleted and current page
      if (deletedType === 'screen') {
        // Force refresh of screen data in AI service
        await aiService.refreshData('screens', currentUser.uid)
        
        // If we're on the screens page, trigger a navigation refresh
        if (currentPath === '/screens') {
          // Force a small delay to ensure the deletion is processed
          await new Promise(resolve => setTimeout(resolve, 1000))
          
          // Trigger a navigation event to refresh the screens page
          window.dispatchEvent(new CustomEvent('screens-refresh', { 
            detail: { timestamp: Date.now() } 
          }))
        }
      } else if (deletedType === 'device') {
        // Force refresh of device data in AI service
        await aiService.refreshData('devices', currentUser.uid)
        
        // If we're on the devices page, trigger a navigation refresh
        if (currentPath === '/devices') {
          // Force a small delay to ensure the deletion is processed
          await new Promise(resolve => setTimeout(resolve, 1000))
          
          // Trigger a navigation event to refresh the devices page
          window.dispatchEvent(new CustomEvent('devices-refresh', { 
            detail: { timestamp: Date.now() } 
          }))
        }
      }
      
    } catch (error) {
      console.error('Error refreshing page data:', error)
      // Fallback to page reload if refresh functions fail
      window.location.reload()
    }
  }

  // Auto-scroll to bottom
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [messages])

  // OpenAI-powered intent analysis
  const analyzeIntentWithOpenAI = async (message) => {
    try {
      const systemPrompt = `You are an intent analyzer for MyDigiSign, a digital signage platform. Analyze the user's message and return the most appropriate intent.

Available intents:
- create_content: User wants to create content (images, videos, slideshows, dynamic content)
- create_video: User specifically wants to create video content
- create_slideshow: User specifically wants to create slideshow content
- create_dynamic: User specifically wants to create dynamic/real-time content
- analyze_performance: User wants to see performance analytics or statistics
- assign_channel: User wants to assign channels to devices
- channel_management: User wants to manage channels (activate, deactivate, mute, unmute)
- list_channels: User wants to see available channels
- device_management: User wants to manage devices or check device status
- add_device: User wants to add, connect, or register a new device
- list_devices: User wants to see all devices
- delete_device: User wants to delete a device
- optimize_schedule: User wants to optimize scheduling
- generate_image: User wants to generate an AI image
- help: User needs help or wants to know what the system can do
- general: General conversation or unclear intent

Return only the intent name, nothing else.`

      // Use the secure Cloud Function for intent analysis
      const result = await apiService.generateContent(message, 'intent', {
        systemPrompt: systemPrompt,
        maxTokens: 20,
        temperature: 0.3
      })
      
      if (!result.success) {
        return null
      }
      
      const intent = result.content.trim().toLowerCase()
      
      // Validate the intent is one we recognize
      const validIntents = [
        'create_content', 'create_video', 'create_slideshow', 'create_dynamic',
        'analyze_performance', 'assign_channel', 'channel_management', 'list_channels',
        'device_management', 'add_device', 'list_devices', 'delete_device', 'optimize_schedule',
        'generate_image', 'help', 'general'
      ]
      
      return validIntents.includes(intent) ? intent : null
    } catch (error) {
      console.error('OpenAI intent analysis error:', error)
      return null
    }
  }

  const analyzeUserIntent = async (message) => {
    // Use OpenAI intent analysis only for complex or unclear messages
    const lowerMessage = message.toLowerCase()
    const isComplexMessage = lowerMessage.includes('how') || 
                            lowerMessage.includes('what') || 
                            lowerMessage.includes('why') || 
                            lowerMessage.includes('explain') ||
                            lowerMessage.includes('tell me') ||
                            lowerMessage.includes('help me') ||
                            lowerMessage.includes('suggest') ||
                            lowerMessage.includes('recommend') ||
                            lowerMessage.includes('optimize') ||
                            lowerMessage.includes('improve') ||
                            lowerMessage.includes('better') ||
                            lowerMessage.length > 50 // Long messages are likely complex
    
    if (apiService && isComplexMessage) {
      try {
        const openAIIntent = await analyzeIntentWithOpenAI(message)
        if (openAIIntent) {
          return openAIIntent
        }
      } catch (error) {
        console.error('OpenAI intent analysis error:', error)
      }
    }
    
    // Use rule-based analysis for simple commands and quick actions
    
    // Schedule operations (moved to higher priority)
    if (lowerMessage.includes('schedule')) {
      if (lowerMessage.includes('create') || lowerMessage.includes('generate') || lowerMessage.includes('make') || lowerMessage.includes('add')) {
        // Check if a specific device name is provided for schedule creation
        const deviceNameMatch = lowerMessage.match(/(?:create|generate|make|add)(?:\s+a)?\s+schedule\s+to\s+device\s+(.+)/i) ||
                               lowerMessage.match(/(?:create|generate|make|add)(?:\s+a)?\s+schedule\s+for\s+device\s+(.+)/i) ||
                               lowerMessage.match(/(?:create|generate|make|add)(?:\s+a)?\s+schedule\s+on\s+device\s+(.+)/i) ||
                               lowerMessage.match(/(?:create|generate|make|add)(?:\s+a)?\s+schedule\s+to\s+(.+)/i) ||
                               lowerMessage.match(/(?:create|generate|make|add)(?:\s+a)?\s+schedule(?:\s+for)?(?:\s+device)?\s+(.+)/i) ||
                               lowerMessage.match(/(?:create|generate|make|add)(?:\s+a)?\s+schedule(?:\s+on)?(?:\s+device)?\s+(.+)/i)
        if (deviceNameMatch && deviceNameMatch[1].trim()) {
          return 'create_schedule_specific'
        }
        return 'create_schedule'
      }
      if (lowerMessage.includes('edit') || lowerMessage.includes('modify') || lowerMessage.includes('change') || lowerMessage.includes('update')) {
        // Check if a specific device name is provided for schedule modification
        const deviceNameMatch = lowerMessage.match(/(?:edit|modify|change|update)\s+schedule\s+to\s+device\s+(.+)/i) ||
                               lowerMessage.match(/(?:edit|modify|change|update)\s+schedule\s+for\s+device\s+(.+)/i) ||
                               lowerMessage.match(/(?:edit|modify|change|update)\s+schedule\s+on\s+device\s+(.+)/i) ||
                               lowerMessage.match(/(?:edit|modify|change|update)\s+schedule\s+to\s+(.+)/i) ||
                               lowerMessage.match(/(?:edit|modify|change|update)(?:\s+of)?\s+schedule(?:\s+for)?(?:\s+device)?\s+(.+)/i) ||
                               lowerMessage.match(/(?:edit|modify|change|update)\s+schedule(?:\s+from)?(?:\s+device)?\s+(.+)/i) ||
                               lowerMessage.match(/(?:edit|modify|change|update)\s+(.+)\s+(?:device|tv|display)\s+schedule/i)
        if (deviceNameMatch && deviceNameMatch[1].trim()) {
          return 'modify_schedule_specific'
        }
        return 'modify_schedule'
      }
      if (lowerMessage.includes('delete') || lowerMessage.includes('remove')) {
        // Check if a specific device name is provided for schedule deletion
        const deviceNameMatch = lowerMessage.match(/(?:delete|remove)\s+schedule\s+from\s+device\s+(.+)/i) ||
                               lowerMessage.match(/(?:delete|remove)\s+schedule\s+to\s+device\s+(.+)/i) ||
                               lowerMessage.match(/(?:delete|remove)\s+schedule\s+for\s+device\s+(.+)/i) ||
                               lowerMessage.match(/(?:delete|remove)\s+schedule\s+on\s+device\s+(.+)/i) ||
                               lowerMessage.match(/(?:delete|remove)\s+schedule\s+to\s+(.+)/i) ||
                               lowerMessage.match(/(?:delete|remove)(?:\s+of)?\s+schedule(?:\s+for)?(?:\s+device)?\s+(.+)/i) ||
                               lowerMessage.match(/(?:delete|remove)(?:\s+of)?\s+schedule(?:\s+from)?(?:\s+device)?\s+(.+)/i) ||
                               lowerMessage.match(/(?:delete|remove)\s+schedule(?:\s+on)?(?:\s+device)?\s+(.+)/i) ||
                               lowerMessage.match(/(?:delete|remove)\s+(.+)\s+(?:device|tv|display)\s+schedule/i)
        if (deviceNameMatch && deviceNameMatch[1].trim()) {
          return 'delete_schedule_specific'
        }
        return 'delete_schedule'
      }
    }

    // Content creation
    if (lowerMessage.includes('create') || lowerMessage.includes('generate') || lowerMessage.includes('make')) {
      if (lowerMessage.includes('screen') || lowerMessage.includes('content')) return 'create_content'
      if (lowerMessage.includes('image') || lowerMessage.includes('picture')) return 'generate_image'
      if (lowerMessage.includes('video') || lowerMessage.includes('clip')) return 'create_video'
      if (lowerMessage.includes('slideshow') || lowerMessage.includes('slides')) return 'create_slideshow'
      if (lowerMessage.includes('dynamic') || lowerMessage.includes('dynamic content')) return 'create_dynamic'
    }
    
    // Analysis
    if (lowerMessage.includes('analyze') || lowerMessage.includes('performance') || lowerMessage.includes('stats')) {
      return 'analyze_performance'
    }
    
    // Optimization
    if (lowerMessage.includes('optimize') || lowerMessage.includes('improve') || lowerMessage.includes('better')) {
      if (lowerMessage.includes('schedule')) return 'optimize_schedule'
      if (lowerMessage.includes('content')) return 'optimize_content'
      if (lowerMessage.includes('system')) return 'optimize_system'
      return 'optimize_general'
    }
    
    // Delete operations
    if (lowerMessage.includes('delete') || lowerMessage.includes('remove') || lowerMessage.includes('remove')) {
      if (lowerMessage.includes('screen') || lowerMessage.includes('content')) {
        // Check if a specific screen name is provided - more flexible patterns
        const screenNameMatch = lowerMessage.match(/(?:delete|remove)(?:\s+of)?\s+(?:screen|content)\s+(.+)/i) ||
                               lowerMessage.match(/(?:delete|remove)\s+(.+)\s+(?:screen|content)/i)
        if (screenNameMatch && screenNameMatch[1].trim()) {
          return 'delete_screen_specific'
        }
        return 'delete_screen'
      }
      if (lowerMessage.includes('device') || lowerMessage.includes('tv') || lowerMessage.includes('display')) {
        // Check if a specific device name is provided - more flexible patterns
        const deviceNameMatch = lowerMessage.match(/(?:delete|remove)(?:\s+of)?\s+(?:device|tv|display)\s+(.+)/i) ||
                               lowerMessage.match(/(?:delete|remove)\s+(.+)\s+(?:device|tv|display)/i)
        if (deviceNameMatch && deviceNameMatch[1].trim()) {
          return 'delete_device_specific'
        }
        return 'delete_device'
      }
      return 'delete_general'
    }

    // Modify operations
    if (lowerMessage.includes('edit') || lowerMessage.includes('modify') || lowerMessage.includes('change') || lowerMessage.includes('update')) {
      if (lowerMessage.includes('screen') || lowerMessage.includes('content')) {
        // Check if a specific screen name is provided - more flexible patterns
        const screenNameMatch = lowerMessage.match(/(?:edit|modify|change|update)(?:\s+of)?\s+(?:screen|content)\s+(.+)/i) ||
                               lowerMessage.match(/(?:edit|modify|change|update)\s+(.+)\s+(?:screen|content)/i)
        if (screenNameMatch && screenNameMatch[1].trim()) {
          return 'modify_screen_specific'
        }
        return 'modify_screen'
      }
      if (lowerMessage.includes('device') || lowerMessage.includes('tv') || lowerMessage.includes('display')) {
        // Check if a specific device name is provided - more flexible patterns
        const deviceNameMatch = lowerMessage.match(/(?:edit|modify|change|update)(?:\s+of)?\s+(?:device|tv|display)\s+(.+)/i) ||
                               lowerMessage.match(/(?:edit|modify|change|update)\s+(.+)\s+(?:device|tv|display)/i)
        if (deviceNameMatch && deviceNameMatch[1].trim()) {
          return 'modify_device_specific'
        }
        return 'modify_device'
      }
      return 'modify_general'
    }
    
    // Device management
    if (lowerMessage.includes('device') || lowerMessage.includes('online') || lowerMessage.includes('offline')) {
      if (lowerMessage.includes('add') || lowerMessage.includes('connect') || lowerMessage.includes('register') || lowerMessage.includes('create')) {
        return 'add_device'
      }
      return 'device_status'
    }
    
    // Channel management
    if (lowerMessage.includes('channel') || lowerMessage.includes('tv') || lowerMessage.includes('stream')) {
      if (lowerMessage.includes('assign') || lowerMessage.includes('connect') || lowerMessage.includes('link')) return 'assign_channel'
      if (lowerMessage.includes('activate') || lowerMessage.includes('turn on') || lowerMessage.includes('enable')) return 'activate_channel'
      if (lowerMessage.includes('deactivate') || lowerMessage.includes('turn off') || lowerMessage.includes('disable')) return 'deactivate_channel'
      if (lowerMessage.includes('mute') || lowerMessage.includes('unmute')) return 'toggle_channel_mute'
      if (lowerMessage.includes('list') || lowerMessage.includes('show') || lowerMessage.includes('view')) return 'list_channels'
      return 'channel_management'
    }
    
    // Recommendations
    if (lowerMessage.includes('recommend') || lowerMessage.includes('suggestion') || lowerMessage.includes('advice')) {
      return 'get_recommendations'
    }
    
    // System status
    if (lowerMessage.includes('status') || lowerMessage.includes('health') || lowerMessage.includes('system')) {
      return 'system_status'
    }
    
    // Troubleshooting
    if (lowerMessage.includes('problem') || lowerMessage.includes('issue') || lowerMessage.includes('error') || lowerMessage.includes('fix')) {
      return 'troubleshoot'
    }

    // Preview operations
    if (lowerMessage.includes('preview') || lowerMessage.includes('show') || lowerMessage.includes('view')) {
      if (lowerMessage.includes('device') || lowerMessage.includes('tv') || lowerMessage.includes('display')) {
        // Check if a specific device name is provided - more flexible patterns
        const deviceNameMatch = lowerMessage.match(/(?:preview|show|view)(?:\s+of)?\s+(?:device|tv|display)\s+(.+)/i) ||
                               lowerMessage.match(/(?:preview|show|view)\s+(.+)\s+(?:device|tv|display)/i)
        if (deviceNameMatch && deviceNameMatch[1].trim()) {
          return 'preview_device_specific'
        }
        return 'preview_device'
      }
      if (lowerMessage.includes('screen') || lowerMessage.includes('content')) {
        // Check if a specific screen name is provided - more flexible patterns
        const screenNameMatch = lowerMessage.match(/(?:preview|show|view)(?:\s+of)?\s+(?:screen|content)\s+(.+)/i) ||
                               lowerMessage.match(/(?:preview|show|view)\s+(.+)\s+(?:screen|content)/i)
        if (screenNameMatch && screenNameMatch[1].trim()) {
          return 'preview_screen_specific'
        }
        return 'preview_screen'
      }
    }
    
    // Dynamic content generation

    // API-powered features
    
    // Help
    if (lowerMessage.includes('help') || lowerMessage.includes('what can you do')) {
      return 'help'
    }

    // List operations
    if (lowerMessage.includes('list') || lowerMessage.includes('show all') || lowerMessage.includes('view all')) {
      if (lowerMessage.includes('device') || lowerMessage.includes('tv') || lowerMessage.includes('display')) {
        return 'list_devices'
      }
      if (lowerMessage.includes('screen') || lowerMessage.includes('content')) {
        return 'list_screens'
      }
    }
    
    // Determine the type of content creation request
    if (lowerMessage.includes('image screen') || lowerMessage.includes('upload image')) {
      return 'create_image_screen'
    }
    
    if (lowerMessage.includes('video clip') || lowerMessage.includes('video screen') || lowerMessage.includes('upload video')) {
      return 'create_video_screen'
    }
    
    if (lowerMessage.includes('slideshow') || lowerMessage.includes('multiple images')) {
      return 'create_slideshow'
    }
    
    return 'general'
  }

  // Generate OpenAI-powered contextual response for open questions
  const generateOpenAIResponse = async (message, intent, analytics) => {
    try {
      const systemContext = `Current System Context:
- Devices: ${analytics.totalDevices} total, ${analytics.onlineDevices} online (${analytics.onlinePercentage}%)
- Screens: ${analytics.totalScreens} active
- Channels: ${analytics.totalChannels} total, ${analytics.activeChannels} active
- Performance Score: ${analytics.performanceScore}/100
- Current Page: ${context}

User Intent: ${intent}

Recent Conversation Context:
${messages.slice(-4).map(msg => `${msg.type === 'user' ? 'User' : 'Assistant'}: ${msg.content}`).join('\n')}`

      const result = await apiService.generateConversationalResponse(message, {
        systemContext
      })

      if (result.success) {
        // Generate contextual actions based on intent
        const actions = generateContextualActions(intent, context, message)
        
        return {
          content: result.content,
          actions
        }
      }
      
      return null // Fall back to predefined responses
    } catch (error) {
      console.error('OpenAI response generation error:', error)
      return null // Fall back to predefined responses
    }
  }

  // Extract screen name from user message
  const extractScreenName = (message) => {
    const lowerMessage = message.toLowerCase()
    
    // Patterns to match screen names
    const patterns = [
      /(?:create|make|add)\s+(?:a\s+)?(?:screen|content|image|video|slideshow|dynamic)\s+(?:named|called|with\s+name|titled)\s+["']?([^"'\n]+)["']?/i,
      /(?:create|make|add)\s+(?:a\s+)?(?:screen|content|image|video|slideshow|dynamic)\s+["']?([^"'\n]+)["']?/i,
      /(?:named|called|with\s+name|titled)\s+["']?([^"'\n]+)["']?/i
    ]
    
    for (const pattern of patterns) {
      const match = message.match(pattern)
      if (match && match[1]) {
        const screenName = match[1].trim()
        // Filter out common words that aren't screen names
        const commonWords = ['screen', 'content', 'image', 'video', 'slideshow', 'dynamic', 'named', 'called', 'with', 'name', 'titled']
        if (!commonWords.includes(screenName.toLowerCase()) && screenName.length > 0) {
          return screenName
        }
      }
    }
    
    return null
  }

  // Determine specific content type from message
  const determineContentType = (message) => {
    const lowerMessage = message.toLowerCase()
    
    // Check for specific content types
    if (lowerMessage.includes('image') || lowerMessage.includes('picture') || lowerMessage.includes('photo')) {
      return 'image'
    }
    if (lowerMessage.includes('video') || lowerMessage.includes('clip') || lowerMessage.includes('movie')) {
      return 'video'
    }
    if (lowerMessage.includes('slideshow') || lowerMessage.includes('slides') || lowerMessage.includes('gallery')) {
      return 'slides'
    }
    if (lowerMessage.includes('dynamic') || lowerMessage.includes('real-time') || lowerMessage.includes('live') || 
        lowerMessage.includes('weather') || lowerMessage.includes('news') || lowerMessage.includes('social')) {
      return 'dynamic'
    }
    
    return null
  }

  // Extract device name from user message
  const extractDeviceName = (message) => {
    const lowerMessage = message.toLowerCase()
    
    // Patterns to match device names
    const patterns = [
      /(?:add|connect|register|create)\s+(?:a\s+)?(?:device|tv|display|screen)\s+(?:named|called|with\s+name|titled)\s+["']?([^"'\n]+)["']?/i,
      /(?:add|connect|register|create)\s+(?:a\s+)?(?:device|tv|display|screen)\s+["']?([^"'\n]+)["']?/i,
      /(?:named|called|with\s+name|titled)\s+["']?([^"'\n]+)["']?/i
    ]
    
    for (const pattern of patterns) {
      const match = message.match(pattern)
      if (match && match[1]) {
        const deviceName = match[1].trim()
        // Filter out common words that aren't device names
        const commonWords = ['device', 'tv', 'display', 'screen', 'named', 'called', 'with', 'name', 'titled', 'add', 'connect', 'register', 'create']
        if (!commonWords.includes(deviceName.toLowerCase()) && deviceName.length > 0) {
          return deviceName
        }
      }
    }
    
    return null
  }

  // Check if message is an open question that should use OpenAI
  const shouldUseOpenAI = (message, intent) => {
    const lowerMessage = message.toLowerCase()
    
    // Use OpenAI for open questions and natural conversation
    const openQuestionPatterns = [
      'how', 'what', 'why', 'when', 'where', 'which', 'who',
      'explain', 'tell me', 'describe', 'help me understand',
      'what if', 'how can', 'what should', 'why is', 'how do',
      'suggest', 'recommend', 'advise', 'tips', 'best practices',
      'compare', 'difference', 'pros and cons', 'advantages',
      'optimize', 'improve', 'enhance', 'better', 'strategies'
    ]
    
    // Use OpenAI for general conversation and complex requests
    const isOpenQuestion = openQuestionPatterns.some(pattern => 
      lowerMessage.includes(pattern)
    )
    
    // Use OpenAI for general intent (unclear or conversational)
    const isGeneralIntent = intent === 'general'
    
    // Use OpenAI for help requests
    const isHelpRequest = intent === 'help'
    
    // Use OpenAI for performance analysis (complex insights)
    const isPerformanceAnalysis = intent === 'analyze_performance'
    
    // Use OpenAI for optimization requests
    const isOptimizationRequest = intent === 'optimize_schedule' || 
                                 intent === 'optimize_content' || 
                                 intent === 'optimize_system' ||
                                 intent === 'optimize_general'
    
    // Use OpenAI for recommendations
    const isRecommendationRequest = intent === 'get_recommendations'
    
    // Use OpenAI for troubleshooting
    const isTroubleshooting = intent === 'troubleshoot'
    
    return isOpenQuestion || isGeneralIntent || isHelpRequest || 
           isPerformanceAnalysis || isOptimizationRequest || 
           isRecommendationRequest || isTroubleshooting
  }

  // Generate contextual actions based on intent and current page
  const generateContextualActions = (intent, currentPage, message = '') => {
    const baseActions = [
      { label: 'View Analytics', action: () => navigate('/analytics') },
      { label: 'Manage Devices', action: () => navigate('/devices') },
      { label: 'Create Content', action: () => navigate('/screens') }
    ]

    // Extract screen name from message
    const screenName = extractScreenName(message)

    switch (intent) {
      case 'create_content':
      case 'create_video':
      case 'create_slideshow':
      case 'create_dynamic':
        return [
          { label: 'Create Image Screen', action: () => navigate('/screens', { state: { openCreateModal: 'image', ...(screenName && { screenName }) } }) },
          { label: 'Create Video Clip', action: () => navigate('/screens', { state: { openCreateModal: 'video', ...(screenName && { screenName }) } }) },
          { label: 'Create Slideshow', action: () => navigate('/screens', { state: { openCreateModal: 'slides', ...(screenName && { screenName }) } }) },
          { label: 'Create Dynamic Content', action: () => navigate('/screens', { state: { openCreateModal: 'dynamic', ...(screenName && { screenName }) } }) }
        ]
      
      case 'analyze_performance':
        return [
          { label: 'View Analytics', action: () => navigate('/analytics') },
          { label: 'Check Devices', action: () => navigate('/devices') },
          { label: 'Manage Channels', action: () => navigate('/channels') },
          { label: 'View Recommendations', action: () => setInputValue('Show me optimization recommendations') }
        ]
      
      case 'assign_channel':
      case 'channel_management':
        return [
          { label: 'Manage Channels', action: () => navigate('/channels') },
          { label: 'Assign to Devices', action: () => navigate('/devices') },
          { label: 'View Channel Analytics', action: () => navigate('/analytics') }
        ]
      
      case 'device_management':
        return [
          { label: 'Manage Devices', action: () => navigate('/devices') },
          { label: 'Check Status', action: () => navigate('/devices') },
          { label: 'Assign Content', action: () => navigate('/screens') }
        ]
      
      case 'add_device':
        return [
          { label: 'Add Device', action: () => navigate('/devices', { state: { openAddDevice: true } }) },
          { label: 'View All Devices', action: () => navigate('/devices') },
          { label: 'Manage Devices', action: () => setInputValue('show me my devices') }
        ]
      
      default:
        return baseActions
    }
  }

  const generateAIResponse = async (message, intent) => {
    const analytics = systemData?.analytics || {
      totalDevices: 0,
      onlineDevices: 0,
      offlineDevices: 0,
      totalScreens: 0,
      totalChannels: 0,
      activeChannels: 0,
      mutedChannels: 0,
      devicesUsingChannels: 0,
      devicesUsingScreens: 0,
      totalSchedules: 0,
      repetitiveSchedules: 0,
      regularSchedules: 0,
      onlinePercentage: 0,
      channelUtilization: 0,
      screenUtilization: 0,
      avgSchedulesPerDevice: 0,
      avgRepetitivePerDevice: 0,
      avgRegularPerDevice: 0,
      performanceScore: 0
    }
    
    if (!aiService) {
      return {
        content: 'AI service is initializing. Please try again in a moment.',
        actions: []
      }
    }

    // Use OpenAI only for open questions and complex requests
    if (apiService && shouldUseOpenAI(message, intent)) {
      const openAIResponse = await generateOpenAIResponse(message, intent, analytics)
      if (openAIResponse) {
        return openAIResponse
      }
    }

    // Handle API-powered features
    if (apiService && intent.startsWith('get_') || intent === 'generate_image') {
      return await handleAPIRequest(message, intent)
    }
    
    switch (intent) {
      case 'create_content':
        const screenName = extractScreenName(message)
        const contentType = determineContentType(message)
        
        // If we can determine the specific content type, open that modal directly
        if (contentType) {
          // Auto-navigate to the specific modal
          setTimeout(() => {
            navigate('/screens', { state: { openCreateModal: contentType, ...(screenName && { screenName }) } })
          }, 100)
          
          const contentTypeLabels = {
            'image': 'Single Image',
            'video': 'Video Clip', 
            'slides': 'Slideshow',
            'dynamic': 'Dynamic Content'
          }
          
          return {
            content: `Perfect! I'll open the ${contentTypeLabels[contentType]} creation modal for you.${screenName ? `\n\nI've pre-filled the screen name as "${screenName}".` : ''}`,
            actions: [
              { label: 'View All Screens', action: () => navigate('/screens') },
              { label: 'Create Different Type', action: () => setInputValue('create content') }
            ]
          }
        }
        
        // If content type is unclear, show all options
        return {
          content: `I can help you create content! Based on your current setup, you have ${analytics.totalScreens} screens and ${analytics.totalDevices} devices.\n\nAvailable content types:\n• **Single Image**: Static images for announcements, promotions, or information\n• **Video Clip**: Video content for engaging displays\n• **Slideshow**: Multiple images that rotate automatically\n• **Dynamic Content**: Real-time content like weather, news, or social media feeds${screenName ? `\n\nI'll pre-fill the screen name as "${screenName}" for you.` : ''}`,
          actions: [
            { label: 'Create Image Screen', action: () => navigate('/screens', { state: { openCreateModal: 'image', ...(screenName && { screenName }) } }) },
            { label: 'Create Video Clip', action: () => navigate('/screens', { state: { openCreateModal: 'video', ...(screenName && { screenName }) } }) },
            { label: 'Create Slideshow', action: () => navigate('/screens', { state: { openCreateModal: 'slides', ...(screenName && { screenName }) } }) },
            { label: 'Create Dynamic Content', action: () => navigate('/screens', { state: { openCreateModal: 'dynamic', ...(screenName && { screenName }) } }) }
          ]
        }
      
      case 'create_video':
        const videoScreenName = extractScreenName(message)
        // Auto-open video creation modal
        setTimeout(() => {
          navigate('/screens', { state: { openCreateModal: 'video', ...(videoScreenName && { screenName: videoScreenName }) } })
        }, 100)
        
        return {
          content: `Perfect! I'll open the Video Clip creation modal for you.${videoScreenName ? `\n\nI've pre-filled the screen name as "${videoScreenName}".` : ''}\n\nVideo clips are great for:\n• Product demonstrations\n• Company announcements\n• Training materials\n• Entertainment content`,
          actions: [
            { label: 'View All Screens', action: () => navigate('/screens') },
            { label: 'Create Different Type', action: () => setInputValue('create content') }
          ]
        }
      
      case 'create_slideshow':
        const slideshowScreenName = extractScreenName(message)
        // Auto-open slideshow creation modal
        setTimeout(() => {
          navigate('/screens', { state: { openCreateModal: 'slides', ...(slideshowScreenName && { screenName: slideshowScreenName }) } })
        }, 100)
        
        return {
          content: `Perfect! I'll open the Slideshow creation modal for you.${slideshowScreenName ? `\n\nI've pre-filled the screen name as "${slideshowScreenName}".` : ''}\n\nSlideshows are great for:\n• Product showcases\n• Event photo galleries\n• Information displays\n• Promotional content`,
          actions: [
            { label: 'View All Screens', action: () => navigate('/screens') },
            { label: 'Create Different Type', action: () => setInputValue('create content') }
          ]
        }
      
      case 'create_dynamic':
        const dynamicScreenName = extractScreenName(message)
        // Auto-open dynamic content creation modal
        setTimeout(() => {
          navigate('/screens', { state: { openCreateModal: 'dynamic', ...(dynamicScreenName && { screenName: dynamicScreenName }) } })
        }, 100)
        
        return {
          content: `Perfect! I'll open the Dynamic Content creation modal for you.${dynamicScreenName ? `\n\nI've pre-filled the screen name as "${dynamicScreenName}".` : ''}\n\nDynamic content is great for:\n• Live weather updates\n• News feeds\n• Social media content\n• Real-time data\n• RSS feeds`,
          actions: [
            { label: 'View All Screens', action: () => navigate('/screens') },
            { label: 'Create Different Type', action: () => setInputValue('create content') }
          ]
        }

      case 'add_device':
        // Auto-open device addition modal
        setTimeout(() => {
          navigate('/devices', { state: { openAddDevice: true } })
        }, 100)
        
        return {
          content: `Perfect! I'll open the device connection modal for you.\n\nTo connect a device:\n1. Enter the device code from your display\n2. The device will be automatically registered\n3. You can then assign content to it`,
          actions: [
            { label: 'View All Devices', action: () => navigate('/devices') },
            { label: 'Manage Devices', action: () => setInputValue('show me my devices') }
          ]
        }
      
      case 'analyze_performance':
        const insights = aiService?.generatePerformanceInsights ? aiService.generatePerformanceInsights() : []
        const systemStatus = aiService?.getSystemStatus ? aiService.getSystemStatus() : { summary: 'System status unavailable' }
        const contentStrategy = aiService?.getContentStrategyInsights ? aiService.getContentStrategyInsights() : null
        
        let performanceContent = `Here's your current performance overview:\n\n📊 **Devices**: ${analytics.totalDevices} total, ${analytics.onlineDevices} online (${analytics.onlinePercentage}%)\n📺 **Screens**: ${analytics.totalScreens} active\n📺 **Channels**: ${analytics.totalChannels} total, ${analytics.activeChannels} active\n📅 **Schedules**: ${analytics.totalSchedules} total (${analytics.avgSchedulesPerDevice} avg per device)\n🎯 **Performance Score**: ${analytics.performanceScore}/100`
        
        if (contentStrategy) {
          performanceContent += `\n\n📈 **Content Strategy**: ${contentStrategy.strategy} (${contentStrategy.channelUtilization}% channels, ${contentStrategy.screenUtilization}% screens)`
        }
        
        performanceContent += `\n\n${systemStatus.summary}\n\n${insights.length > 0 ? `\nKey Insights:\n${insights.map(i => `• ${i.title}: ${i.description}`).join('\n')}` : ''}`
        
        return {
          content: performanceContent,
          actions: [
            { label: 'View Analytics', action: () => navigate('/analytics') },
            { label: 'Check Devices', action: () => navigate('/devices') },
            { label: 'Manage Channels', action: () => navigate('/channels') },
            { label: 'View Recommendations', action: () => setInputValue('Show me optimization recommendations') }
          ]
        }
      
      case 'optimize_schedule':
        const schedulingSuggestions = aiService?.generateSchedulingSuggestions ? aiService.generateSchedulingSuggestions() : [
          { title: 'Increase Content Rotation', description: 'Rotate content more frequently to maintain engagement' },
          { title: 'Add Peak Time Content', description: 'Schedule important content during 9-11 AM and 4-6 PM' },
          { title: 'Optimize Schedule Density', description: 'Add more schedules per device for better coverage' }
        ]
        
        let scheduleInfo = ''
        if (analytics.totalSchedules > 0) {
          const repetitivePercent = analytics.totalSchedules > 0 ? Math.round((analytics.repetitiveSchedules / analytics.totalSchedules) * 100) : 0
          const regularPercent = analytics.totalSchedules > 0 ? Math.round((analytics.regularSchedules / analytics.totalSchedules) * 100) : 0
          
          scheduleInfo = `⏰ **Peak Times**: 9-11 AM and 4-6 PM show highest engagement\n📱 **Device Usage**: ${analytics.avgSchedulesPerDevice} schedules per device\n🔄 **Schedule Mix**: ${repetitivePercent}% repetitive, ${regularPercent}% regular schedules\n📊 **Optimization**: Balanced mix of repetitive and regular schedules improves engagement by 30%`
        } else {
          scheduleInfo = `⏰ **Peak Times**: 9-11 AM and 4-6 PM show highest engagement\n📱 **Scheduling**: Set up scheduling system to optimize content delivery\n🔄 **Schedule Types**: Consider both repetitive (daily/weekly) and regular (one-time) schedules\n📊 **Best Practice**: Mix of schedule types provides optimal content variety`
        }
        
        return {
          content: `Based on your data, here are some scheduling optimization suggestions:\n\n${scheduleInfo}\n\n${schedulingSuggestions.map(s => `• ${s.title}: ${s.description}`).join('\n')}`,
          actions: [
            { label: 'View Schedules', action: () => navigate('/schedule') },
            { label: 'Add New Schedule', action: () => navigate('/schedule', { state: { openAddSchedule: true } }) }
          ]
        }
      
      case 'device_status':
        const deviceHealth = analytics.deviceHealth || []
        const offlineDevices = deviceHealth.filter(d => d.status === 'offline')
        return {
          content: `Device Status Report:\n\n🟢 **Online**: ${analytics.onlineDevices} devices\n🔴 **Offline**: ${analytics.offlineDevices} devices\n📊 **Uptime**: ${analytics.onlinePercentage}%\n\n${offlineDevices.length > 0 ? `Offline Devices:\n${offlineDevices.map(d => `• ${d.name} (last seen: ${d.lastSeen ? d.lastSeen.toLocaleString() : 'Never'})`).join('\n')}\n\n` : ''}${analytics.onlinePercentage < 80 ? '⚠️ Some devices may need attention. Check connectivity and power status.' : '✅ All devices are performing well!'}`,
          actions: [
            { label: 'Manage Devices', action: () => navigate('/devices') },
            { label: 'Add Device', action: () => navigate('/devices', { state: { openAddDevice: true } }) }
          ]
        }
      
      case 'get_recommendations':
        const recommendations = aiService?.generateOptimizationRecommendations ? aiService.generateOptimizationRecommendations() : [
          { title: 'Add More Content', priority: 'medium', description: 'Create more screens to improve engagement', impact: 'medium' },
          { title: 'Check Device Connectivity', priority: 'high', description: 'Ensure all devices are properly connected', impact: 'high' },
          { title: 'Optimize Schedules', priority: 'medium', description: 'Add more scheduled content for better coverage', impact: 'medium' }
        ]
        return {
          content: `Based on your current system status, here are my recommendations:\n\n${recommendations.map(r => `🔸 **${r.title}** (${r.priority} priority)\n${r.description}\nImpact: ${r.impact}\n`).join('\n')}`,
          actions: [
            { label: 'View All Recommendations', action: () => navigate('/analytics') },
            { label: 'Check Devices', action: () => navigate('/devices') },
            { label: 'Create Content', action: () => navigate('/screens') }
          ]
        }
      
      case 'system_status':
        const currentSystemStatus = aiService.getSystemStatus()
        const statusAlerts = currentSystemStatus.alerts
        return {
          content: `System Status Overview:\n\n🎯 **Overall**: ${currentSystemStatus.status.overall.toUpperCase()}\n📱 **Devices**: ${currentSystemStatus.status.devices.toUpperCase()}\n📺 **Content**: ${currentSystemStatus.status.content.toUpperCase()}\n📅 **Schedules**: ${currentSystemStatus.status.schedules.toUpperCase()}\n\n${currentSystemStatus.summary}\n\n${statusAlerts.length > 0 ? `Active Alerts:\n${statusAlerts.map(a => `⚠️ ${a.title}: ${a.message}`).join('\n')}` : '✅ No active alerts'}`,
          actions: [
            { label: 'View Detailed Status', action: () => navigate('/analytics') },
            { label: 'Check Devices', action: () => navigate('/devices') },
            { label: 'Manage Content', action: () => navigate('/screens') }
          ]
        }
      
      case 'troubleshoot':
        const troubleshootDeviceHealth = analytics.deviceHealth || []
        const troubleshootOfflineDevices = troubleshootDeviceHealth.filter(d => d.status === 'offline')
        const commonIssues = []
        
        if (troubleshootOfflineDevices.length > 0) {
          commonIssues.push('• Device connectivity issues - check power and network connections')
        }
        if (analytics.totalScreens === 0) {
          commonIssues.push('• No content available - create screens to display')
        }
        if (analytics.totalSchedules === 0) {
          commonIssues.push('• No schedules configured - set up content scheduling')
        }
        
        return {
          content: `Let me help you troubleshoot your system:\n\n${commonIssues.length > 0 ? `Common Issues Detected:\n${commonIssues.join('\n')}\n\n` : ''}Troubleshooting Steps:\n1. Check device power and network connections\n2. Verify content is properly uploaded\n3. Ensure schedules are configured correctly\n4. Check system logs for errors\n\nWould you like me to help you resolve any specific issues?`,
          actions: [
            { label: 'Check Device Status', action: () => navigate('/devices') },
            { label: 'View System Logs', action: () => navigate('/analytics') },
            { label: 'Create Test Content', action: () => navigate('/screens') }
          ]
        }
      
      case 'optimize_system':
        const systemOptimizationRecommendations = aiService.generateOptimizationRecommendations()
        const currentPerformanceScore = analytics.performanceScore || 0
        return {
          content: `System Optimization Analysis:\n\n🎯 **Current Performance Score**: ${currentPerformanceScore}/100\n\n${currentPerformanceScore < 70 ? 'Your system needs optimization. Here are the key areas to focus on:\n\n' : 'Your system is performing well! Here are some optimization opportunities:\n\n'}${systemOptimizationRecommendations.map(r => `• ${r.title}: ${r.description}`).join('\n')}\n\nOptimization Priority:\n1. Device connectivity (${analytics.onlinePercentage}% uptime)\n2. Content variety (${analytics.totalScreens} screens)\n3. Schedule optimization (${analytics.avgSchedulesPerDevice} avg per device)`,
          actions: [
            { label: 'View Optimization Plan', action: () => navigate('/analytics') },
            { label: 'Check Devices', action: () => navigate('/devices') },
            { label: 'Create More Content', action: () => navigate('/screens') }
          ]
        }
      
      case 'generate_image':
        if (!apiService) {
          return {
            content: 'Image generation requires API configuration. Please set up your OpenAI API key.',
            actions: [
              { label: 'Setup API Keys', action: () => navigate('/settings') },
              { label: 'View Setup Guide', action: () => window.open('/API_KEYS_SETUP.md', '_blank') }
            ]
          }
        }
        return {
          content: `I can generate custom images for your digital signage! What type of image would you like me to create?\n\nExamples:\n• "Generate a restaurant menu background"\n• "Create a promotional banner for happy hour"\n• "Make a welcome sign for a retail store"`,
          actions: [
            { label: 'Generate Menu Image', action: () => setInputValue('Generate a professional restaurant menu background') },
            { label: 'Create Promotional Banner', action: () => setInputValue('Generate a promotional banner for restaurant specials') },
            { label: 'Make Welcome Sign', action: () => setInputValue('Generate a welcoming sign for visitors') }
          ]
        }
      
      case 'delete_screen':
        return {
          content: `I can help you delete a screen! Please provide the name of the screen you want to delete.\n\nExamples:\n• "Delete screen Welcome Message"\n• "Remove screen Promotional Banner"\n• "Delete the screen called Restaurant Menu"`,
          actions: [
            { label: 'List All Screens', action: () => navigate('/screens') },
            { label: 'Cancel', action: () => setInputValue('') }
          ]
        }
      
      case 'delete_screen_specific':
        return await handleDeleteScreen(message)
      
      case 'delete_device':
        return {
          content: `I can help you delete a device! Please provide the name of the device you want to delete.\n\nExamples:\n• "Delete device Living Room TV"\n• "Remove device Office Display"\n• "Delete the device called Reception Screen"`,
          actions: [
            { label: 'List All Devices', action: () => navigate('/devices') },
            { label: 'Cancel', action: () => setInputValue('') }
          ]
        }
      
      case 'delete_device_specific':
        return await handleDeleteDevice(message)
      
      case 'modify_device':
        return {
          content: `I can help you modify a device! Please provide the name of the device you want to edit.\n\nExamples:\n• "Edit device Living Room TV"\n• "Modify device Office Display"\n• "Change device Reception Screen"`,
          actions: [
            { label: 'List All Devices', action: () => navigate('/devices') },
            { label: 'Cancel', action: () => setInputValue('') }
          ]
        }
      
      case 'modify_device_specific':
        return await handleModifyDevice(message)
      
      case 'modify_screen':
        return {
          content: `I can help you modify a screen! Please provide the name of the screen you want to edit.\n\nExamples:\n• "Edit screen Welcome Message"\n• "Modify screen Promotional Banner"\n• "Change screen Restaurant Menu"`,
          actions: [
            { label: 'List All Screens', action: () => navigate('/screens') },
            { label: 'Cancel', action: () => setInputValue('') }
          ]
        }
      
      case 'modify_screen_specific':
        return await handleModifyScreen(message)
      
      case 'create_schedule':
        return {
          content: `I can help you create a schedule! Please provide the device name you want to schedule content for.\n\nExamples:\n• "Create schedule for device Living Room TV"\n• "Add schedule to device Office Display"\n• "Make schedule on device Reception Screen"\n• "Create schedule to device fgt"`,
          actions: [
            { label: 'List All Devices', action: () => navigate('/devices') },
            { label: 'Cancel', action: () => setInputValue('') }
          ]
        }
      
      case 'create_schedule_specific':
        return await handleCreateSchedule(message)
      
      case 'modify_schedule':
        return {
          content: `I can help you modify a schedule! Please provide the device name that has the schedule you want to edit.\n\nExamples:\n• "Edit schedule for device Living Room TV"\n• "Modify schedule on device Office Display"\n• "Change schedule for device Reception Screen"\n• "Modify schedule to device fgt"`,
          actions: [
            { label: 'List All Schedules', action: () => navigate('/schedule') },
            { label: 'Cancel', action: () => setInputValue('') }
          ]
        }
      
      case 'modify_schedule_specific':
        return await handleModifySchedule(message)
      
      case 'delete_schedule':
        return {
          content: `I can help you delete a schedule! Please provide the device name that has the schedule you want to remove.\n\nExamples:\n• "Delete schedule for device Living Room TV"\n• "Remove schedule from device Office Display"\n• "Delete schedule on device Reception Screen"\n• "Delete schedule to device fgt"`,
          actions: [
            { label: 'List All Schedules', action: () => navigate('/schedule') },
            { label: 'Cancel', action: () => setInputValue('') }
          ]
        }
      
      case 'delete_schedule_specific':
        return await handleDeleteSchedule(message)
      
      case 'delete_general':
        return {
          content: `I can help you delete screens or devices. What would you like to delete?\n\n• **Screens**: Content displays like images, videos, or slideshows\n• **Devices**: Physical displays like TVs or monitors`,
          actions: [
            { label: 'Delete Screen', action: () => setInputValue('Delete screen ') },
            { label: 'Delete Device', action: () => setInputValue('Delete device ') },
            { label: 'View Screens', action: () => navigate('/screens') },
            { label: 'View Devices', action: () => navigate('/devices') }
          ]
        }

      case 'preview_device':
        return {
          content: `I can help you preview a device! Please provide the name of the device you want to preview.\n\nExamples:\n• "Preview device Living Room TV"\n• "Show device Office Display"\n• "View device Reception Screen"`,
          actions: [
            { label: 'List All Devices', action: () => navigate('/devices') },
            { label: 'Cancel', action: () => setInputValue('') }
          ]
        }
      
      case 'preview_device_specific':
        return await handlePreviewDevice(message)
      
      case 'preview_screen':
        return {
          content: `I can help you preview a screen! Please provide the name of the screen you want to preview.\n\nExamples:\n• "Preview screen Welcome Message"\n• "Show screen Promotional Banner"\n• "View screen Restaurant Menu"`,
          actions: [
            { label: 'List All Screens', action: () => navigate('/screens') },
            { label: 'Cancel', action: () => setInputValue('') }
          ]
        }
      
      case 'preview_screen_specific':
        return await handlePreviewScreen(message)

      case 'list_devices':
        return await handleListDevices()
      
      case 'list_screens':
        return await handleListScreens()
      
      case 'help':
        return {
          content: `Quick Actions Available:\n\n🎨 **Create Content**: Single images, videos, slideshows, dynamic content\n📺 **Manage Channels**: Assign channels to devices\n📊 **View Analytics**: Performance insights\n📱 **Manage Devices**: Monitor, edit, and control devices\n📅 **Manage Schedules**: Create, modify, and delete schedules\n👁️ **Preview**: View screens and devices\n🗑️ **Delete**: Remove screens and devices\n🖼️ **AI Images**: Generate images with AI\n\n**Quick Commands:**\n• "List devices" - Show all devices\n• "List screens" - Show all screens\n• "List channels" - Show available channels\n• "Edit device [name]" - Modify device settings\n• "Delete device [name]" - Remove a device\n• "Edit screen [name]" - Modify screen content\n• "Delete screen [name]" - Remove a screen\n• "Create schedule for device [name]" - Create new schedule\n• "Edit schedule for device [name]" - Modify schedule\n• "Delete schedule for device [name]" - Remove schedule\n• "Create image screen" - Create new image content\n• "Generate image" - Create AI-generated images\n\nFor detailed explanations and complex questions, just ask naturally!`,
          actions: [
            { label: 'Create Image Screen', action: () => setInputValue('Create an image screen') },
            { label: 'Create Video Clip', action: () => setInputValue('Create a video clip') },
            { label: 'Create Slideshow', action: () => setInputValue('Create a slideshow') },
            { label: 'List Devices', action: () => setInputValue('List devices') },
            { label: 'List Screens', action: () => setInputValue('List screens') },
            { label: 'Edit Device', action: () => setInputValue('Edit device ') },
            { label: 'Delete Device', action: () => setInputValue('Delete device ') },
            { label: 'Edit Screen', action: () => setInputValue('Edit screen ') },
            { label: 'Delete Screen', action: () => setInputValue('Delete screen ') },
            { label: 'Create Schedule', action: () => setInputValue('Create schedule for device ') },
            { label: 'Edit Schedule', action: () => setInputValue('Edit schedule for device ') },
            { label: 'Delete Schedule', action: () => setInputValue('Delete schedule for device ') },
            { label: 'Generate Image', action: () => setInputValue('Generate a restaurant menu background') }
          ]
        }
      
      case 'assign_channel':
        return {
          content: `I can help you assign channels to devices! Channels provide live content streaming and can be assigned to any number of devices.\n\nBased on your current setup, you have ${analytics.totalChannels} available channels and ${analytics.totalDevices} devices.\n\nAvailable channels include:\n• News channels for current events\n• Weather channels for local forecasts\n• Sports channels for live coverage\n• Entertainment channels for music and videos`,
          actions: [
            { label: 'Manage Channels', action: () => navigate('/channels') },
            { label: 'Manage Devices', action: () => navigate('/devices') },
            { label: 'Channel Analytics', action: () => setInputValue('Show me channel analytics') }
          ]
        }
      
      case 'channel_management':
        const channelAnalytics = aiService?.getChannelAnalytics ? aiService.getChannelAnalytics() : null
        const channelManagementSuggestions = aiService?.generateChannelManagementSuggestions ? aiService.generateChannelManagementSuggestions() : []
        
        let channelInfo = ''
        if (channelAnalytics) {
          channelInfo = `📺 **Available Channels**: ${channelAnalytics.total}\n🟢 **Active**: ${channelAnalytics.active}\n🔇 **Muted**: ${channelAnalytics.muted}\n💎 **Premium**: ${channelAnalytics.premium}\n📱 **Devices Using Channels**: ${channelAnalytics.devicesUsingChannels}\n📊 **Avg Devices per Channel**: ${channelAnalytics.averageDevicesPerChannel.toFixed(1)}`
        } else {
          channelInfo = `📺 **Available Channels**: ${analytics.totalChannels}\n🟢 **Active**: ${analytics.activeChannels}\n🔇 **Muted**: ${analytics.mutedChannels}\n📱 **Channel Utilization**: ${analytics.channelUtilization}%`
        }
        
        return {
          content: `Channel Management Overview:\n\n${channelInfo}\n\nYou can assign these pre-configured channels to your devices for live content streaming.\n\n${channelManagementSuggestions.length > 0 ? `\nRecommendations:\n${channelManagementSuggestions.map(s => `• ${s.title}: ${s.description}`).join('\n')}` : ''}`,
          actions: [
            { label: 'Manage Channels', action: () => navigate('/channels') },
            { label: 'Manage Devices', action: () => navigate('/devices') },
            { label: 'Channel Analytics', action: () => setInputValue('Show me channel analytics') }
          ]
        }
      
      case 'list_channels':
        const channels = systemData?.channels || []
        if (channels.length === 0) {
          return {
            content: `No channels are currently available. Channels are pre-configured and provided by the system.`,
            actions: [
              { label: 'Manage Channels', action: () => navigate('/channels') },
              { label: 'Manage Devices', action: () => navigate('/devices') }
            ]
          }
        }
        
        const channelList = channels.map(ch => {
          const status = ch.isActive ? '🟢 Active' : '🔴 Inactive'
          const muteStatus = ch.isMuted ? '🔇 Muted' : '🔊 Unmuted'
          return `• ${ch.name} (${status}, ${muteStatus})`
        }).join('\n')
        
        return {
          content: `Here are the available channels:\n\n${channelList}\n\nTotal: ${channels.length} channels\n\nYou can assign these channels to your devices for live content streaming.`,
          actions: [
            { label: 'Manage Channels', action: () => navigate('/channels') },
            { label: 'Manage Devices', action: () => navigate('/devices') },
            { label: 'Channel Analytics', action: () => setInputValue('Show me channel analytics') }
          ]
        }
      
      case 'activate_channel':
        return {
          content: `I can help you activate channels! Activating a channel will start streaming live content to assigned devices.\n\nYou can activate channels from the Channels page. Would you like me to take you there?`,
          actions: [
            { label: 'Manage Channels', action: () => navigate('/channels') },
            { label: 'View Active Channels', action: () => setInputValue('List my channels') }
          ]
        }
      
      case 'deactivate_channel':
        return {
          content: `I can help you deactivate channels! Deactivating a channel will stop streaming content to assigned devices.\n\nYou can manage channel activation from the Channels page. Would you like me to take you there?`,
          actions: [
            { label: 'Manage Channels', action: () => navigate('/channels') },
            { label: 'View Active Channels', action: () => setInputValue('List my channels') }
          ]
        }
      
      case 'toggle_channel_mute':
        return {
          content: `I can help you mute or unmute channels! Muting a channel will stop audio while keeping the video stream active.\n\nYou can manage channel mute settings from the Channels page. Would you like me to take you there?`,
          actions: [
            { label: 'Manage Channels', action: () => navigate('/channels') },
            { label: 'View Muted Channels', action: () => setInputValue('List my channels') }
          ]
        }
      
      case 'delete_channel':
        return {
          content: `Channels are pre-configured and provided by the system, so they cannot be deleted. However, you can:\n\n• Assign or unassign channels from devices\n• Activate or deactivate channels\n• Mute or unmute channels\n• View channel analytics`,
          actions: [
            { label: 'Manage Channels', action: () => navigate('/channels') },
            { label: 'Manage Devices', action: () => navigate('/devices') },
            { label: 'List Channels', action: () => setInputValue('List my channels') }
          ]
        }
      
      default:
        return {
          content: `I can help you with quick actions and tasks. For complex questions or detailed explanations, just ask naturally!\n\n**Quick Actions:** Create content, manage devices/channels, view analytics, preview items, delete items, find stock images.`,
          actions: [
            { label: 'Create Content', action: () => navigate('/screens') },
            { label: 'Manage Channels', action: () => navigate('/channels') },
            { label: 'View Analytics', action: () => navigate('/analytics') },
            { label: 'Manage Devices', action: () => navigate('/devices') },
            { label: 'Help', action: () => setInputValue('help') }
          ]
        }
    }
  }

  const handleSendMessage = async () => {
    if (!inputValue.trim()) return

    // Check if user is on free plan and show upgrade modal
    if (isFreePlanUser(currentUserData)) {
      setShowUpgradeModal(true)
      return
    }

    const userMessage = {
      id: Date.now(),
      type: 'user',
      content: inputValue,
      timestamp: new Date()
    }

    setMessages(prev => [...prev, userMessage])
    setInputValue('')
    setIsLoading(true)

    try {
      const intent = await analyzeUserIntent(inputValue)
      const aiResponse = await generateAIResponse(inputValue, intent)
      
      const responseMessage = {
        id: Date.now() + 1,
        type: 'ai',
        content: aiResponse.content,
        timestamp: new Date(),
        actions: aiResponse.actions || []
      }
      
      setMessages(prev => [...prev, responseMessage])
    } catch (error) {
      console.error('Error generating AI response:', error)
      const errorMessage = {
        id: Date.now() + 1,
        type: 'ai',
        content: 'Sorry, I encountered an error. Please try again.',
        timestamp: new Date(),
        actions: []
      }
      setMessages(prev => [...prev, errorMessage])
    } finally {
      setIsLoading(false)
    }
  }

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      handleSendMessage()
    }
  }

  const handleActionClick = (action) => {
    if (action.action) {
      action.action()
    }
  }

  // Handle screen deletion
  const handleDeleteScreen = async (message) => {
    if (!aiService || !currentUser?.uid) {
      return {
        content: 'AI service is not available. Please try again.',
        actions: []
      }
    }

    // Extract screen name from message - more flexible patterns
    const screenNameMatch = message.match(/(?:delete|remove)(?:\s+of)?\s+(?:screen|content)\s+(.+)/i) ||
                           message.match(/(?:delete|remove)\s+(.+)\s+(?:screen|content)/i)
    if (!screenNameMatch) {
      return {
        content: 'Please specify which screen you want to delete. For example: "Delete screen Welcome Message" or "Delete of screen qq"',
        actions: [
          { label: 'List Screens', action: () => navigate('/screens') },
          { label: 'Try Again', action: () => setInputValue('Delete screen ') }
        ]
      }
    }

    const screenName = screenNameMatch[1].trim()
    
    // Find the screen first using the service
    const foundScreen = aiService.findScreenByName(screenName)
    if (!foundScreen) {
      return {
        content: `Screen "${screenName}" not found. Please check the name and try again.\n\n💡 **Tip**: You can say "List screens" to see all available screens.`,
        actions: [
          { label: 'List All Screens', action: () => navigate('/screens') },
          { label: 'Try Again', action: () => setInputValue(`Delete screen ${screenName}`) }
        ]
      }
    }

    // If multiple matches found, show them with better formatting
    if (Array.isArray(foundScreen)) {
      return {
        content: `Multiple screens found matching "${screenName}":\n\n${foundScreen.map(screen => `• **${screen.name}** (${screen.type || 'Unknown type'})`).join('\n')}\n\nPlease be more specific about which screen to delete.`,
        actions: [
          { label: 'List All Screens', action: () => navigate('/screens') },
          { label: 'Try Again', action: () => setInputValue(`Delete screen ${screenName}`) }
        ]
      }
    }

    // Show screen details before deletion
    const screenType = foundScreen.type || 'Unknown'
    const isDynamic = foundScreen.isDynamic || false
    
    let contentType = 'No content'
    if (foundScreen.imageUrl) {
      contentType = 'Image'
    } else if (foundScreen.videoUrl) {
      contentType = 'Video'
    } else if (foundScreen.slidesData && foundScreen.slidesData.length > 0) {
      contentType = `Slideshow (${foundScreen.slidesData.length} slides)`
    } else if (isDynamic) {
      contentType = 'Dynamic Content'
    }
    
    const assignedDevices = aiService.systemData.devices?.filter(d => d.screenId === foundScreen.key) || []
    const hasSchedules = aiService.systemData.schedules?.some(s => s.screenId === foundScreen.key) || false

    let warningMessage = ''
    let currentAssignment = 'No devices assigned'
    if (assignedDevices.length > 0) {
      currentAssignment = `${assignedDevices.length} device(s) assigned`
      warningMessage += `\n⚠️ **Warning**: This screen is assigned to ${assignedDevices.length} device(s).`
    }
    if (hasSchedules) {
      warningMessage += `\n⚠️ **Warning**: This screen has active schedules.`
    }

    // Navigate to screens page with delete modal open
    navigate('/screens', { 
      state: { 
        deleteScreen: foundScreen.key,
        screenName: foundScreen.name
      } 
    })

    // Confirm deletion with detailed information
    return {
      content: `🗑️ **Delete Screen Confirmation**\n\n**Screen**: ${foundScreen.name}\n**Type**: ${screenType}\n**Content**: ${contentType}\n**Dynamic**: ${isDynamic ? 'Yes' : 'No'}\n**Current Assignment**: ${currentAssignment}\n**Created**: ${foundScreen.created ? new Date(foundScreen.created).toLocaleDateString() : 'Unknown'}${warningMessage}\n\nThis action will:\n• Delete the screen permanently\n• Remove it from all assigned devices\n• Cancel any active schedules\n• Delete associated files\n\n**This action cannot be undone.**\n\n🔄 **Opening screen deletion dialog...**`,
      actions: [
        { label: 'View Screen Deletion Dialog', action: () => navigate('/screens') },
        { label: 'Delete Another Screen', action: () => setInputValue('Delete screen ') },
        { label: 'List All Screens', action: () => navigate('/screens') }
      ]
    }
  }

  // Handle device deletion
  const handleDeleteDevice = async (message) => {
    if (!aiService || !currentUser?.uid) {
      return {
        content: 'AI service is not available. Please try again.',
        actions: []
      }
    }

    // Extract device name from message - more flexible patterns
    const deviceNameMatch = message.match(/(?:delete|remove)(?:\s+of)?\s+(?:device|tv|display)\s+(.+)/i) ||
                           message.match(/(?:delete|remove)\s+(.+)\s+(?:device|tv|display)/i)
    
    if (!deviceNameMatch) {
      return {
        content: 'Please specify which device you want to delete. For example: "Delete device Living Room TV" or "Delete of device qq"',
        actions: [
          { label: 'List Devices', action: () => navigate('/devices') },
          { label: 'Try Again', action: () => setInputValue('Delete device ') }
        ]
      }
    }

    const deviceName = deviceNameMatch[1].trim()
    
    // Find the device first using the service
    const foundDevice = aiService.findDeviceByName(deviceName)
    
    if (!foundDevice) {
      return {
        content: `Device "${deviceName}" not found. Please check the name and try again.\n\n💡 **Tip**: You can say "List devices" to see all available devices.`,
        actions: [
          { label: 'List All Devices', action: () => navigate('/devices') },
          { label: 'Try Again', action: () => setInputValue(`Delete device ${deviceName}`) }
        ]
      }
    }

    // If multiple matches found, show them with better formatting
    if (Array.isArray(foundDevice)) {
      return {
        content: `Multiple devices found matching "${deviceName}":\n\n${foundDevice.map(device => `• **${device.name}** (${device.model || 'Unknown model'})`).join('\n')}\n\nPlease be more specific about which device to delete.`,
        actions: [
          { label: 'List All Devices', action: () => navigate('/devices') },
          { label: 'Try Again', action: () => setInputValue(`Delete device ${deviceName}`) }
        ]
      }
    }

    // Show device details before deletion
    const assignedScreen = aiService.systemData.screens?.find(s => s.key === foundDevice.screenId)
    const assignedChannel = aiService.systemData.channels?.find(c => c.key === foundDevice.channelId)
    const hasSchedules = aiService.systemData.schedules?.some(s => s.deviceId === foundDevice.key) || false
    const isOnline = foundDevice.isOnline || false

    let warningMessage = ''
    let currentAssignment = 'No content assigned'
    if (assignedScreen) {
      currentAssignment = `Screen: ${assignedScreen.name}`
      warningMessage += `\n⚠️ **Warning**: This device is assigned to screen "${assignedScreen.name}".`
    } else if (assignedChannel) {
      currentAssignment = `Channel: ${assignedChannel.name} (LiveTV)`
      warningMessage += `\n⚠️ **Warning**: This device is assigned to channel "${assignedChannel.name}".`
    }
    if (hasSchedules) {
      warningMessage += `\n⚠️ **Warning**: This device has active schedules.`
    }
    if (isOnline) {
      warningMessage += `\n⚠️ **Warning**: This device is currently online.`
    }

    // Navigate to devices page with delete modal open
    navigate('/devices', { 
      state: { 
        deleteDevice: foundDevice.key,
        deviceName: foundDevice.name
      } 
    })

    // Confirm deletion with detailed information
    return {
      content: `🗑️ **Delete Device Confirmation**\n\n**Device**: ${foundDevice.name}\n**Model**: ${foundDevice.model || 'Unknown'}\n**Status**: ${isOnline ? '🟢 Online' : '🔴 Offline'}\n**Current Assignment**: ${currentAssignment}\n**Last Seen**: ${foundDevice.lastAccess ? new Date(foundDevice.lastAccess).toLocaleDateString() : 'Never'}${warningMessage}\n\nThis action will:\n• Delete the device permanently\n• Remove all associated schedules\n• Unassign any connected screen/channel\n• Clear device history\n\n**This action cannot be undone.**\n\n🔄 **Opening device deletion dialog...**`,
      actions: [
        { label: 'View Device Deletion Dialog', action: () => navigate('/devices') },
        { label: 'Delete Another Device', action: () => setInputValue('Delete device ') },
        { label: 'List All Devices', action: () => navigate('/devices') }
      ]
    }
  }

  // Handle screen modification
  const handleModifyScreen = async (message) => {
    if (!aiService || !currentUser?.uid) {
      return {
        content: 'AI service is not available. Please try again.',
        actions: []
      }
    }

    // Extract screen name from message - more flexible patterns
    const screenNameMatch = message.match(/(?:edit|modify|change|update)(?:\s+of)?\s+(?:screen|content)\s+(.+)/i) ||
                           message.match(/(?:edit|modify|change|update)\s+(.+)\s+(?:screen|content)/i)
    if (!screenNameMatch) {
      return {
        content: 'Please specify which screen you want to modify. For example: "Edit screen Welcome Message" or "Modify screen Promotional Banner"',
        actions: [
          { label: 'List Screens', action: () => navigate('/screens') },
          { label: 'Try Again', action: () => setInputValue('Edit screen ') }
        ]
      }
    }

    const screenName = screenNameMatch[1].trim()
    
    // Find the screen first using the service
    const foundScreen = aiService.findScreenByName(screenName)
    if (!foundScreen) {
      return {
        content: `Screen "${screenName}" not found. Please check the name and try again.\n\n💡 **Tip**: You can say "List screens" to see all available screens.`,
        actions: [
          { label: 'List All Screens', action: () => navigate('/screens') },
          { label: 'Try Again', action: () => setInputValue(`Edit screen ${screenName}`) }
        ]
      }
    }

    // If multiple matches found, show them with better formatting
    if (Array.isArray(foundScreen)) {
      return {
        content: `Multiple screens found matching "${screenName}":\n\n${foundScreen.map(screen => `• **${screen.name}** (${screen.type || 'Unknown type'})`).join('\n')}\n\nPlease be more specific about which screen to modify.`,
        actions: [
          { label: 'List All Screens', action: () => navigate('/screens') },
          { label: 'Try Again', action: () => setInputValue(`Edit screen ${screenName}`) }
        ]
      }
    }

    // Show screen details and modification options
    const screenType = foundScreen.type || 'Unknown'
    const isDynamic = foundScreen.isDynamic || false
    const hasContent = foundScreen.imageUrl || foundScreen.videoUrl || foundScreen.slidesData

    let contentType = 'No content'
    if (foundScreen.imageUrl) {
      contentType = 'Image'
    } else if (foundScreen.videoUrl) {
      contentType = 'Video'
    } else if (foundScreen.slidesData && foundScreen.slidesData.length > 0) {
      contentType = `Slideshow (${foundScreen.slidesData.length} slides)`
    } else if (isDynamic) {
      contentType = 'Dynamic Content'
    }

    // Navigate to screens page with edit modal open
    navigate('/screens', { 
      state: { 
        editScreen: foundScreen.key,
        screenName: foundScreen.name
      } 
    })

    return {
      content: `✏️ **Screen Modification**\n\n**Screen**: ${foundScreen.name}\n**Type**: ${screenType}\n**Content**: ${contentType}\n**Dynamic**: ${isDynamic ? 'Yes' : 'No'}\n**Created**: ${foundScreen.created ? new Date(foundScreen.created).toLocaleDateString() : 'Unknown'}\n\n🔄 **Opening screen editor...**\n\nYou can now modify:\n• Screen name and settings\n• Content (images, videos, slideshows)\n• Dynamic content configuration\n• Display preferences\n\nThe screen editor has been opened in a new window.`,
      actions: [
        { label: 'View Screen Editor', action: () => navigate('/screens') },
        { label: 'Modify Another Screen', action: () => setInputValue('Edit screen ') },
        { label: 'List All Screens', action: () => navigate('/screens') }
      ]
    }
  }

  // Handle schedule creation
  const handleCreateSchedule = async (message) => {
    if (!aiService || !currentUser?.uid) {
      return {
        content: 'AI service is not available. Please try again.',
        actions: []
      }
    }

    // Extract device name from message - more flexible patterns
    const deviceNameMatch = message.match(/(?:create|generate|make|add)(?:\s+a)?\s+schedule\s+to\s+device\s+(.+)/i) ||
                           message.match(/(?:create|generate|make|add)(?:\s+a)?\s+schedule\s+for\s+device\s+(.+)/i) ||
                           message.match(/(?:create|generate|make|add)(?:\s+a)?\s+schedule\s+on\s+device\s+(.+)/i) ||
                           message.match(/(?:create|generate|make|add)(?:\s+a)?\s+schedule\s+to\s+(.+)/i) ||
                           message.match(/(?:create|generate|make|add)(?:\s+a)?\s+schedule(?:\s+for)?(?:\s+device)?\s+(.+)/i) ||
                           message.match(/(?:create|generate|make|add)(?:\s+a)?\s+schedule(?:\s+on)?(?:\s+device)?\s+(.+)/i)
    if (!deviceNameMatch) {
      return {
        content: 'Please specify which device you want to create a schedule for. For example: "Create schedule for device Living Room TV" or "Add schedule to device Office Display"',
        actions: [
          { label: 'List Devices', action: () => navigate('/devices') },
          { label: 'Try Again', action: () => setInputValue('Create schedule for device ') }
        ]
      }
    }

    const deviceName = deviceNameMatch[1].trim()
    
    // Find the device first using the service
    const foundDevice = aiService.findDeviceByName(deviceName)
    if (!foundDevice) {
      return {
        content: `Device "${deviceName}" not found. Please check the name and try again.\n\n💡 **Tip**: You can say "List devices" to see all available devices.`,
        actions: [
          { label: 'List All Devices', action: () => navigate('/devices') },
          { label: 'Try Again', action: () => setInputValue(`Create schedule for device ${deviceName}`) }
        ]
      }
    }

    // If multiple matches found, show them with better formatting
    if (Array.isArray(foundDevice)) {
      return {
        content: `Multiple devices found matching "${deviceName}":\n\n${foundDevice.map(device => `• **${device.name}** (${device.isOnline ? '🟢 Online' : '🔴 Offline'})`).join('\n')}\n\nPlease be more specific about which device to create a schedule for.`,
        actions: [
          { label: 'List All Devices', action: () => navigate('/devices') },
          { label: 'Try Again', action: () => setInputValue(`Create schedule for device ${deviceName}`) }
        ]
      }
    }

    // Show device details and available screens for scheduling
    const deviceStatus = foundDevice.isOnline ? '🟢 Online' : '🔴 Offline'
    const currentScreen = foundDevice.screenId ? aiService.systemData.screens?.find(s => s.key === foundDevice.screenId) : null
    const availableScreens = aiService.systemData.screens || []
    
    let currentContent = 'No content assigned'
    if (currentScreen) {
      const contentType = currentScreen.imageUrl ? 'Image' : 
                         currentScreen.videoUrl ? 'Video' : 
                         currentScreen.slidesData ? 'Slideshow' : 'Dynamic'
      currentContent = `${currentScreen.name} (${contentType})`
    }

    // Navigate to schedule page with device pre-selected
    navigate('/schedule', { 
      state: { 
        createScheduleForDevice: foundDevice.key,
        deviceName: foundDevice.name
      } 
    })

    return {
      content: `📅 **Create Schedule**\n\n**Device**: ${foundDevice.name}\n**Status**: ${deviceStatus}\n**Current Content**: ${currentContent}\n**Available Screens**: ${availableScreens.length}\n\n🔄 **Opening schedule creation dialog...**\n\nYou can now create a schedule that will:\n• Display content at specific times\n• Run on specific days of the week\n• Automatically switch between different screens\n• Set up recurring schedules\n\nThe schedule creation dialog has been opened with this device pre-selected.`,
      actions: [
        { label: 'View Schedule Creator', action: () => navigate('/schedule') },
        { label: 'Create Another Schedule', action: () => setInputValue('Create schedule for device ') },
        { label: 'List All Devices', action: () => navigate('/devices') }
      ]
    }
  }

  // Handle schedule modification
  const handleModifySchedule = async (message) => {
    if (!aiService || !currentUser?.uid) {
      return {
        content: 'AI service is not available. Please try again.',
        actions: []
      }
    }

    // Extract device name from message - more flexible patterns
    const deviceNameMatch = message.match(/(?:edit|modify|change|update)\s+schedule\s+to\s+device\s+(.+)/i) ||
                           message.match(/(?:edit|modify|change|update)\s+schedule\s+for\s+device\s+(.+)/i) ||
                           message.match(/(?:edit|modify|change|update)\s+schedule\s+on\s+device\s+(.+)/i) ||
                           message.match(/(?:edit|modify|change|update)\s+schedule\s+to\s+(.+)/i) ||
                           message.match(/(?:edit|modify|change|update)(?:\s+of)?\s+schedule(?:\s+for)?(?:\s+device)?\s+(.+)/i) ||
                           message.match(/(?:edit|modify|change|update)\s+schedule(?:\s+from)?(?:\s+device)?\s+(.+)/i) ||
                           message.match(/(?:edit|modify|change|update)\s+(.+)\s+(?:device|tv|display)\s+schedule/i)
    if (!deviceNameMatch) {
      return {
        content: 'Please specify which device has the schedule you want to modify. For example: "Edit schedule for device Living Room TV" or "Modify schedule from device Office Display"',
        actions: [
          { label: 'List Schedules', action: () => navigate('/schedule') },
          { label: 'Try Again', action: () => setInputValue('Edit schedule for device ') }
        ]
      }
    }

    const deviceName = deviceNameMatch[1].trim()
    
    // Find the device first using the service
    const foundDevice = aiService.findDeviceByName(deviceName)
    if (!foundDevice) {
      return {
        content: `Device "${deviceName}" not found. Please check the name and try again.\n\n💡 **Tip**: You can say "List devices" to see all available devices.`,
        actions: [
          { label: 'List All Devices', action: () => navigate('/devices') },
          { label: 'Try Again', action: () => setInputValue(`Edit schedule for device ${deviceName}`) }
        ]
      }
    }

    // If multiple matches found, show them with better formatting
    if (Array.isArray(foundDevice)) {
      return {
        content: `Multiple devices found matching "${deviceName}":\n\n${foundDevice.map(device => `• **${device.name}** (${device.isOnline ? '🟢 Online' : '🔴 Offline'})`).join('\n')}\n\nPlease be more specific about which device's schedule to modify.`,
        actions: [
          { label: 'List All Devices', action: () => navigate('/devices') },
          { label: 'Try Again', action: () => setInputValue(`Edit schedule for device ${deviceName}`) }
        ]
      }
    }

    // Find schedules for this device
    const deviceSchedules = aiService.systemData.schedules?.filter(s => s.deviceId === foundDevice.key) || []
    
    if (deviceSchedules.length === 0) {
      return {
        content: `No schedules found for device "${foundDevice.name}".\n\nThis device doesn't have any active schedules. You can create a new schedule for this device.`,
        actions: [
          { label: 'Create Schedule', action: () => setInputValue(`Create schedule for device ${foundDevice.name}`) },
          { label: 'List All Schedules', action: () => navigate('/schedule') },
          { label: 'List All Devices', action: () => navigate('/devices') }
        ]
      }
    }

    // Show schedule details
    const scheduleInfo = deviceSchedules.map(schedule => {
      const screen = aiService.systemData.screens?.find(s => s.key === schedule.screenId)
      const screenName = screen ? screen.name : 'Unknown Screen'
      const days = schedule.days?.join(', ') || 'All days'
      const timeRange = `${schedule.startTime} - ${schedule.endTime}`
      return `• **${schedule.name}** (${screenName})\n  📅 ${days} | ⏰ ${timeRange}`
    }).join('\n\n')

    // Navigate to schedule page with device pre-selected for modification
    navigate('/schedule', { 
      state: { 
        modifyScheduleForDevice: foundDevice.key,
        deviceName: foundDevice.name
      } 
    })

    return {
      content: `✏️ **Modify Schedule**\n\n**Device**: ${foundDevice.name}\n**Active Schedules**: ${deviceSchedules.length}\n\n${scheduleInfo}\n\n🔄 **Opening schedule modification dialog...**\n\nYou can now modify:\n• Schedule timing and days\n• Content assignments\n• Schedule names and descriptions\n• Enable/disable schedules\n\nThe schedule modification dialog has been opened with this device's schedules.`,
      actions: [
        { label: 'View Schedule Editor', action: () => navigate('/schedule') },
        { label: 'Modify Another Schedule', action: () => setInputValue('Edit schedule for device ') },
        { label: 'List All Schedules', action: () => navigate('/schedule') }
      ]
    }
  }

  // Handle schedule deletion
  const handleDeleteSchedule = async (message) => {
    if (!aiService || !currentUser?.uid) {
      return {
        content: 'AI service is not available. Please try again.',
        actions: []
      }
    }

    // Extract device name from message - more flexible patterns
    const deviceNameMatch = message.match(/(?:delete|remove)\s+schedule\s+from\s+device\s+(.+)/i) ||
                           message.match(/(?:delete|remove)\s+schedule\s+to\s+device\s+(.+)/i) ||
                           message.match(/(?:delete|remove)\s+schedule\s+for\s+device\s+(.+)/i) ||
                           message.match(/(?:delete|remove)\s+schedule\s+on\s+device\s+(.+)/i) ||
                           message.match(/(?:delete|remove)\s+schedule\s+to\s+(.+)/i) ||
                           message.match(/(?:delete|remove)(?:\s+of)?\s+schedule(?:\s+for)?(?:\s+device)?\s+(.+)/i) ||
                           message.match(/(?:delete|remove)(?:\s+of)?\s+schedule(?:\s+from)?(?:\s+device)?\s+(.+)/i) ||
                           message.match(/(?:delete|remove)\s+schedule(?:\s+on)?(?:\s+device)?\s+(.+)/i) ||
                           message.match(/(?:delete|remove)\s+(.+)\s+(?:device|tv|display)\s+schedule/i)
    if (!deviceNameMatch) {
      return {
        content: 'Please specify which device has the schedule you want to delete. For example: "Delete schedule for device Living Room TV" or "Remove schedule from device Office Display"',
        actions: [
          { label: 'List Schedules', action: () => navigate('/schedule') },
          { label: 'Try Again', action: () => setInputValue('Delete schedule for device ') }
        ]
      }
    }

    const deviceName = deviceNameMatch[1].trim()
    
    // Find the device first using the service
    const foundDevice = aiService.findDeviceByName(deviceName)
    if (!foundDevice) {
      return {
        content: `Device "${deviceName}" not found. Please check the name and try again.\n\n💡 **Tip**: You can say "List devices" to see all available devices.`,
        actions: [
          { label: 'List All Devices', action: () => navigate('/devices') },
          { label: 'Try Again', action: () => setInputValue(`Delete schedule for device ${deviceName}`) }
        ]
      }
    }

    // If multiple matches found, show them with better formatting
    if (Array.isArray(foundDevice)) {
      return {
        content: `Multiple devices found matching "${deviceName}":\n\n${foundDevice.map(device => `• **${device.name}** (${device.isOnline ? '🟢 Online' : '🔴 Offline'})`).join('\n')}\n\nPlease be more specific about which device's schedule to delete.`,
        actions: [
          { label: 'List All Devices', action: () => navigate('/devices') },
          { label: 'Try Again', action: () => setInputValue(`Delete schedule for device ${deviceName}`) }
        ]
      }
    }

    // Find schedules for this device
    const deviceSchedules = aiService.systemData.schedules?.filter(s => s.deviceId === foundDevice.key) || []
    
    if (deviceSchedules.length === 0) {
      return {
        content: `No schedules found for device "${foundDevice.name}".\n\nThis device doesn't have any active schedules to delete.`,
        actions: [
          { label: 'Create Schedule', action: () => setInputValue(`Create schedule for device ${foundDevice.name}`) },
          { label: 'List All Schedules', action: () => navigate('/schedule') },
          { label: 'List All Devices', action: () => navigate('/devices') }
        ]
      }
    }

    // Show schedule details for deletion confirmation
    const scheduleInfo = deviceSchedules.map(schedule => {
      const screen = aiService.systemData.screens?.find(s => s.key === schedule.screenId)
      const screenName = screen ? screen.name : 'Unknown Screen'
      const days = schedule.days?.join(', ') || 'All days'
      const timeRange = `${schedule.startTime} - ${schedule.endTime}`
      return `• **${schedule.name}** (${screenName})\n  📅 ${days} | ⏰ ${timeRange}`
    }).join('\n\n')

    // Navigate to schedule page with device pre-selected for deletion
    navigate('/schedule', { 
      state: { 
        deleteScheduleForDevice: foundDevice.key,
        deviceName: foundDevice.name
      } 
    })

    return {
      content: `🗑️ **Delete Schedule**\n\n**Device**: ${foundDevice.name}\n**Schedules to Delete**: ${deviceSchedules.length}\n\n${scheduleInfo}\n\n⚠️ **Warning**: This will permanently delete all schedules for this device.\n\nThis action will:\n• Remove all scheduled content\n• Stop automatic content switching\n• Clear all timing configurations\n\n**This action cannot be undone.**\n\n🔄 **Opening schedule deletion dialog...**`,
      actions: [
        { label: 'View Schedule Deletion Dialog', action: () => navigate('/schedule') },
        { label: 'Delete Another Schedule', action: () => setInputValue('Delete schedule for device ') },
        { label: 'List All Schedules', action: () => navigate('/schedule') }
      ]
    }
  }

  // Handle device modification
  const handleModifyDevice = async (message) => {
    if (!aiService || !currentUser?.uid) {
      return {
        content: 'AI service is not available. Please try again.',
        actions: []
      }
    }

    // Extract device name from message - more flexible patterns
    const deviceNameMatch = message.match(/(?:edit|modify|change|update)(?:\s+of)?\s+(?:device|tv|display)\s+(.+)/i) ||
                           message.match(/(?:edit|modify|change|update)\s+(.+)\s+(?:device|tv|display)/i)
    if (!deviceNameMatch) {
      return {
        content: 'Please specify which device you want to modify. For example: "Edit device Living Room TV" or "Modify device Office Display"',
        actions: [
          { label: 'List Devices', action: () => navigate('/devices') },
          { label: 'Try Again', action: () => setInputValue('Edit device ') }
        ]
      }
    }

    const deviceName = deviceNameMatch[1].trim()
    
    // Find the device first using the service
    const foundDevice = aiService.findDeviceByName(deviceName)
    if (!foundDevice) {
      return {
        content: `Device "${deviceName}" not found. Please check the name and try again.\n\n💡 **Tip**: You can say "List devices" to see all available devices.`,
        actions: [
          { label: 'List All Devices', action: () => navigate('/devices') },
          { label: 'Try Again', action: () => setInputValue(`Edit device ${deviceName}`) }
        ]
      }
    }

    // If multiple matches found, show them with better formatting
    if (Array.isArray(foundDevice)) {
      return {
        content: `Multiple devices found matching "${deviceName}":\n\n${foundDevice.map(device => `• **${device.name}** (${device.model || 'Unknown model'})`).join('\n')}\n\nPlease be more specific about which device to modify.`,
        actions: [
          { label: 'List All Devices', action: () => navigate('/devices') },
          { label: 'Try Again', action: () => setInputValue(`Edit device ${deviceName}`) }
        ]
      }
    }

    // Show device details and modification options
    const assignedScreen = aiService.systemData.screens?.find(s => s.key === foundDevice.screenId)
    const assignedChannel = aiService.systemData.channels?.find(c => c.key === foundDevice.channelId)
    const isOnline = foundDevice.isOnline || false

    let currentAssignment = 'No content assigned'
    if (assignedScreen) {
      currentAssignment = `Screen: ${assignedScreen.name}`
    } else if (assignedChannel) {
      currentAssignment = `Channel: ${assignedChannel.name} (LiveTV)`
    }

    // Navigate to devices page with edit modal open
    navigate('/devices', { 
      state: { 
        editDevice: foundDevice.key,
        deviceName: foundDevice.name
      } 
    })

    return {
      content: `✏️ **Device Modification**\n\n**Device**: ${foundDevice.name}\n**Model**: ${foundDevice.model || 'Unknown'}\n**Status**: ${isOnline ? '🟢 Online' : '🔴 Offline'}\n**Current Assignment**: ${currentAssignment}\n**Last Seen**: ${foundDevice.lastAccess ? new Date(foundDevice.lastAccess).toLocaleDateString() : 'Never'}\n\n🔄 **Opening device editor...**\n\nYou can now modify:\n• Device name\n• Assigned screen or channel\n• Device settings\n\nThe device editor has been opened in a new window.`,
      actions: [
        { label: 'View Device Editor', action: () => navigate('/devices') },
        { label: 'Modify Another Device', action: () => setInputValue('Edit device ') },
        { label: 'List All Devices', action: () => navigate('/devices') }
      ]
    }
  }

  // Handle device preview
  const handlePreviewDevice = async (message) => {
    if (!aiService || !currentUser?.uid) {
      return {
        content: 'AI service is not available. Please try again.',
        actions: []
      }
    }

    // Extract device name from message - more flexible patterns
    const deviceNameMatch = message.match(/(?:preview|show|view)(?:\s+of)?\s+(?:device|tv|display)\s+(.+)/i) ||
                           message.match(/(?:preview|show|view)\s+(.+)\s+(?:device|tv|display)/i)
    if (!deviceNameMatch) {
      return {
        content: 'Please specify which device you want to preview. For example: "Preview device Living Room TV" or "Preview of device qq"',
        actions: [
          { label: 'List Devices', action: () => navigate('/devices') },
          { label: 'Try Again', action: () => setInputValue('Preview device ') }
        ]
      }
    }

    const deviceName = deviceNameMatch[1].trim()
    
    // Debug: Log the search attempt
    console.log('🔍 Searching for device:', deviceName)
    console.log('🔍 Available devices:', aiService.systemData.devices?.map(d => d.name) || [])
    
    // Find the device first using the service
    const foundDevice = aiService.findDeviceByName(deviceName)
    console.log('🔍 Found device:', foundDevice)
    
    if (!foundDevice) {
      return {
        content: `Device "${deviceName}" not found. Please check the name and try again.\n\n💡 **Tip**: You can say "List devices" to see all available devices.\n\n**Available devices**: ${aiService.systemData.devices?.map(d => d.name).join(', ') || 'None'}`,
        actions: [
          { label: 'List All Devices', action: () => navigate('/devices') },
          { label: 'Try Again', action: () => setInputValue(`Preview device ${deviceName}`) }
        ]
      }
    }

    // If multiple matches found, show them with better formatting
    if (Array.isArray(foundDevice)) {
      return {
        content: `Multiple devices found matching "${deviceName}":\n\n${foundDevice.map(device => `• **${device.name}** (${device.model || 'Unknown model'})`).join('\n')}\n\nPlease be more specific about which device to preview.`,
        actions: [
          { label: 'List All Devices', action: () => navigate('/devices') },
          { label: 'Try Again', action: () => setInputValue(`Preview device ${deviceName}`) }
        ]
      }
    }

    // Navigate to devices page with preview state
    return {
      content: `📱 **Device Preview Ready**\n\n**Device**: ${foundDevice.name}\n**Model**: ${foundDevice.model || 'Unknown'}\n**Status**: ${foundDevice.isOnline ? '🟢 Online' : '🔴 Offline'}\n**Last Seen**: ${foundDevice.lastAccess ? new Date(foundDevice.lastAccess).toLocaleString() : 'Never'}\n\nI'll open the devices page and show you a preview of this device.`,
      actions: [
        { 
          label: '📱 Preview Device', 
          action: () => navigate('/devices', { state: { previewDevice: foundDevice.key } })
        },
        { label: 'View All Devices', action: () => navigate('/devices') },
        { label: 'Preview Another', action: () => setInputValue('Preview device ') }
      ]
    }
  }

  // Handle screen preview
  const handlePreviewScreen = async (message) => {
    if (!aiService || !currentUser?.uid) {
      return {
        content: 'AI service is not available. Please try again.',
        actions: []
      }
    }

    // Extract screen name from message - more flexible patterns
    const screenNameMatch = message.match(/(?:preview|show|view)(?:\s+of)?\s+(?:screen|content)\s+(.+)/i) ||
                           message.match(/(?:preview|show|view)\s+(.+)\s+(?:screen|content)/i)
    if (!screenNameMatch) {
      return {
        content: 'Please specify which screen you want to preview. For example: "Preview screen Welcome Message" or "Preview of screen qq"',
        actions: [
          { label: 'List Screens', action: () => navigate('/screens') },
          { label: 'Try Again', action: () => setInputValue('Preview screen ') }
        ]
      }
    }

    const screenName = screenNameMatch[1].trim()
    
    // Find the screen first using the service
    const foundScreen = aiService.findScreenByName(screenName)
    if (!foundScreen) {
      return {
        content: `Screen "${screenName}" not found. Please check the name and try again.\n\n💡 **Tip**: You can say "List screens" to see all available screens.`,
        actions: [
          { label: 'List All Screens', action: () => navigate('/screens') },
          { label: 'Try Again', action: () => setInputValue(`Preview screen ${screenName}`) }
        ]
      }
    }

    // If multiple matches found, show them with better formatting
    if (Array.isArray(foundScreen)) {
      return {
        content: `Multiple screens found matching "${screenName}":\n\n${foundScreen.map(screen => `• **${screen.name}** (${screen.type || 'Unknown type'})`).join('\n')}\n\nPlease be more specific about which screen to preview.`,
        actions: [
          { label: 'List All Screens', action: () => navigate('/screens') },
          { label: 'Try Again', action: () => setInputValue(`Preview screen ${screenName}`) }
        ]
      }
    }

    // Navigate to screens page with preview state
    return {
      content: `📺 **Screen Preview Ready**\n\n**Screen**: ${foundScreen.name}\n**Type**: ${foundScreen.videoUrl ? 'Video Clip' : foundScreen.slides ? 'Slideshow' : foundScreen.imageUrl ? 'Single Image' : 'Unknown'}\n**Created**: ${foundScreen.created ? new Date(foundScreen.created).toLocaleDateString() : 'Unknown'}\n**Default**: ${foundScreen.isDefault ? 'Yes' : 'No'}\n\nI'll open the screens page and show you a preview of this screen.`,
      actions: [
        { 
          label: '📺 Preview Screen', 
          action: () => navigate('/screens', { state: { previewScreen: foundScreen.key } })
        },
        { label: 'View All Screens', action: () => navigate('/screens') },
        { label: 'Preview Another', action: () => setInputValue('Preview screen ') }
      ]
    }
  }

  // Handle listing devices
  const handleListDevices = async () => {
    if (!aiService || !currentUser?.uid) {
      return {
        content: 'AI service is not available. Please try again.',
        actions: []
      }
    }

    const devices = aiService.systemData.devices || []
    
    if (devices.length === 0) {
      return {
        content: '📱 **No devices found**\n\nYou don\'t have any devices connected yet. Add your first device to get started!',
        actions: [
          { label: 'Add Device', action: () => navigate('/devices', { state: { openAddDevice: true } }) },
          { label: 'View Devices Page', action: () => navigate('/devices') }
        ]
      }
    }

    const onlineDevices = devices.filter(d => d.isOnline)
    const offlineDevices = devices.filter(d => !d.isOnline)

    return {
      content: `📱 **Your Devices (${devices.length} total)**\n\n${devices.map(device => 
        `• **${device.name}** ${device.isOnline ? '🟢' : '🔴'} (${device.model || 'Unknown model'})${device.screenId ? ' 📺' : ''}`
      ).join('\n')}\n\n**Status**: ${onlineDevices.length} online, ${offlineDevices.length} offline`,
      actions: [
        { label: 'View All Devices', action: () => navigate('/devices') },
        { label: 'Add New Device', action: () => navigate('/devices', { state: { openAddDevice: true } }) },
        { label: 'Preview Device', action: () => setInputValue('Preview device ') }
      ]
    }
  }

  // Handle listing screens
  const handleListScreens = async () => {
    if (!aiService || !currentUser?.uid) {
      return {
        content: 'AI service is not available. Please try again.',
        actions: []
      }
    }

    const screens = aiService.systemData.screens || []
    
    if (screens.length === 0) {
      return {
        content: '📺 **No screens found**\n\nYou don\'t have any screens created yet. Create your first screen to get started!',
        actions: [
          { label: 'Create Screen', action: () => navigate('/screens', { state: { openCreateModal: 'image' } }) },
          { label: 'View Screens Page', action: () => navigate('/screens') }
        ]
      }
    }

    const imageScreens = screens.filter(s => s.imageUrl && !s.videoUrl && !s.slides)
    const videoScreens = screens.filter(s => s.videoUrl)
    const slideshowScreens = screens.filter(s => s.slides)
    const dynamicScreens = screens.filter(s => s.isDynamic)

    return {
      content: `📺 **Your Screens (${screens.length} total)**\n\n${screens.map(screen => {
        const type = screen.videoUrl ? 'Video Clip' : screen.slides ? 'Slideshow' : screen.imageUrl ? 'Single Image' : 'Unknown'
        // Use same icons as the app
        const icon = screen.isDynamic ? '📈' : screen.videoUrl ? '🎥' : screen.slides ? '📦' : '🖼️'
        return `• **${screen.name}** ${icon} (${type})${screen.isDefault ? ' ⭐' : ''}`
      }).join('\n')}\n\n**Types**: ${imageScreens.length} images, ${videoScreens.length} videos, ${slideshowScreens.length} slideshows, ${dynamicScreens.length} dynamic`,
      actions: [
        { label: 'View All Screens', action: () => navigate('/screens') },
        { label: 'Create New Screen', action: () => navigate('/screens', { state: { openCreateModal: 'image' } }) },
        { label: 'Preview Screen', action: () => setInputValue('Preview screen ') }
      ]
    }
  }

  const handleAPIRequest = async (message, intent) => {
    try {
      switch (intent) {
        case 'delete_screen':
        case 'delete_screen_specific':
          return await handleDeleteScreen(message)
        
        case 'delete_device':
        case 'delete_device_specific':
          return await handleDeleteDevice(message)
        
        case 'preview_device':
        case 'preview_device_specific':
          return await handlePreviewDevice(message)
        
        case 'preview_screen':
        case 'preview_screen_specific':
          return await handlePreviewScreen(message)
        
        case 'list_devices':
          return await handleListDevices()
        
        case 'list_screens':
          return await handleListScreens()
        
        case 'generate_image':
          const imagePrompt = message.replace(/generate|create|make/i, '').trim()
          const imageResult = await apiService.generateImage(imagePrompt, {
            size: '1024x1024',
            style: 'natural'
          })
          
          if (imageResult.success) {
            return {
              content: `🎨 I've generated an image for you!\n\n**Prompt**: ${imagePrompt}\n\n**Image URL**: ${imageResult.images[0]}\n\nYou can use this image in your digital signage content.`,
              actions: [
                { label: 'View Image', action: () => window.open(imageResult.images[0], '_blank') },
                { label: 'Create Screen with Image', action: () => navigate('/screens', { state: { openCreateModal: 'image', imageUrl: imageResult.images[0] } }) },
                { label: 'Generate Another', action: () => setInputValue(`Generate ${imagePrompt} with different style`) }
              ]
            }
          } else {
            return {
              content: `Sorry, I couldn't generate the image. Error: ${imageResult.error}`,
              actions: [
                { label: 'Try Again', action: () => setInputValue(message) }
              ]
            }
          }

        default:
          return {
            content: 'I understand you want to use an API feature, but I need more specific instructions.',
            actions: [
              { label: 'Generate Image', action: () => setInputValue('Generate a restaurant menu background') }
            ]
          }
      }
    } catch (error) {
      console.error('API request error:', error)
      return {
        content: `Sorry, I encountered an error while processing your request: ${error.message}`,
        actions: [
          { label: 'Try Again', action: () => setInputValue(message) },
          { label: 'Check API Setup', action: () => window.open('/API_KEYS_SETUP.md', '_blank') }
        ]
      }
    }
  }

  const getContextualQuickActions = () => {
    const analytics = systemData.analytics
    
    switch (context) {
      case 'screens':
        return [
          'List screens',
          'Create image screen',
          'Create video clip',
          'Create slideshow',
          'Edit screen',
          'Delete screen',
          'Preview screen',
          'View templates'
        ]
      case 'devices':
        return [
          'List devices',
          'Add new device',
          'Edit device',
          'Delete device',
          'Check device status',
          'Preview device',
          'Troubleshoot offline devices',
          'View device analytics'
        ]
      case 'channels':
        return [
          'List channels',
          'Assign channels to devices',
          'Activate channels',
          'Manage channel settings',
          'View channel analytics',
          'Check channel status'
        ]
      case 'schedule':
        return [
          'Create schedule for device',
          'Edit schedule for device',
          'Delete schedule for device',
          'Add new schedule',
          'Optimize existing schedules',
          'Set peak time schedules',
          'Create recurring content'
        ]
      case 'analytics':
        return [
          'Generate performance report',
          'Analyze content performance',
          'Check device uptime',
          'Export analytics data'
        ]
      default:
        return [
          analytics.totalDevices === 0 ? 'Add your first device' : 'Create image screen',
          analytics.totalChannels === 0 ? 'Assign channels to devices' : 'Manage channels',
          analytics.totalSchedules === 0 ? 'Create your first schedule' : 'Optimize schedule',
          analytics.totalDevices > 0 ? 'Edit device' : 'Add device',
          'Create video clip',
          'Analyze performance'
        ]
    }
  }

  const quickActions = getContextualQuickActions()

  return (
    <div className={cn(
      "fixed right-0 top-0 h-full bg-white border-l border-gray-200 shadow-lg transition-all duration-300 z-40",
      isExpanded ? "w-80" : "w-12"
    )}>
      {isExpanded ? (
        <div className="flex flex-col h-full">
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b border-gray-200 bg-gradient-to-r from-purple-500 to-blue-600 text-white">
            <div className="flex items-center space-x-2">
              <Brain className="w-5 h-5" />
              <span className="font-semibold">AI Assistant</span>
              <div className="flex items-center space-x-1 bg-white/10 rounded-full px-2 py-1">
                <Sparkles className="w-3 h-3" />
                <span className="text-xs">Live</span>
              </div>
            </div>
            <div className="flex items-center space-x-1">
              <button
                onClick={() => setIsExpanded(false)}
                className="p-1 rounded hover:bg-white/10 transition-colors"
              >
                <Minimize2 className="w-4 h-4" />
              </button>
            </div>
          </div>

          {/* Messages */}
          <div className="flex-1 overflow-y-auto p-4 space-y-4">
            {messages.map((message) => (
              <div key={message.id}>
                <div
                  className={cn(
                    "flex space-x-3",
                    message.type === 'user' ? "justify-end" : "justify-start"
                  )}
                >
                  {message.type === 'ai' && (
                    <div className="w-8 h-8 bg-purple-100 rounded-full flex items-center justify-center flex-shrink-0">
                      <Bot className="w-4 h-4 text-purple-600" />
                    </div>
                  )}
                  <div
                    className={cn(
                      "max-w-[80%] p-3 rounded-lg",
                      message.type === 'user'
                        ? "bg-primary-500 text-white"
                        : "bg-gray-100 text-gray-900"
                    )}
                  >
                    <p className="text-sm whitespace-pre-line">{message.content}</p>
                    <p className="text-xs opacity-70 mt-1">
                      {message.timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                    </p>
                  </div>
                  {message.type === 'user' && (
                    <div className="w-8 h-8 bg-primary-100 rounded-full flex items-center justify-center flex-shrink-0">
                      <User className="w-4 h-4 text-primary-600" />
                    </div>
                  )}
                </div>
                
                {/* Action buttons for AI messages */}
                {message.type === 'ai' && message.actions && message.actions.length > 0 && (
                  <div className="mt-2 ml-11 space-y-1">
                    {message.actions.map((action, index) => (
                      <button
                        key={index}
                        onClick={() => handleActionClick(action)}
                        className="w-full text-left p-2 text-xs bg-purple-50 hover:bg-purple-100 text-purple-700 rounded-md transition-colors border border-purple-200"
                      >
                        {action.label}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            ))}
            
            {isLoading && (
              <div className="flex space-x-3">
                <div className="w-8 h-8 bg-purple-100 rounded-full flex items-center justify-center flex-shrink-0">
                  <Bot className="w-4 h-4 text-purple-600" />
                </div>
                <div className="bg-gray-100 p-3 rounded-lg">
                  <div className="flex items-center space-x-2">
                    <Loader2 className="w-4 h-4 animate-spin text-purple-600" />
                    <span className="text-sm text-gray-600">AI is thinking...</span>
                  </div>
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          {/* Quick Actions */}
          <div className="p-4 border-t border-gray-200">
            <p className="text-xs font-medium text-gray-600 mb-2">Quick Actions</p>
            <div className="space-y-2">
              {quickActions.map((action, index) => (
                <button
                  key={index}
                  onClick={() => setInputValue(action)}
                  className="w-full text-left p-2 text-xs bg-gray-50 hover:bg-gray-100 rounded-md transition-colors"
                >
                  {action}
                </button>
              ))}
            </div>
          </div>

          {/* Input */}
          <div className="p-4 border-t border-gray-200">
            <div className="flex space-x-2">
              <input
                type="text"
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder="Ask me anything..."
                className="flex-1 px-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                disabled={isLoading}
              />
              <button
                onClick={handleSendMessage}
                disabled={!inputValue.trim() || isLoading}
                className="px-3 py-2 bg-purple-500 text-white rounded-md hover:bg-purple-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                <Send className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      ) : (
        /* Collapsed state */
        <div className="h-full flex flex-col items-center justify-center">
          <button
            onClick={() => setIsExpanded(true)}
            className="p-2 hover:bg-gray-100 rounded-md transition-colors"
          >
            <Maximize2 className="w-6 h-6 text-gray-600" />
          </button>
          <div className="mt-2 text-center">
            <Brain className="w-6 h-6 text-purple-500 mx-auto mb-1" />
            <span className="text-xs text-gray-500">AI</span>
          </div>
        </div>
      )}
      
      {/* Subscription Upgrade Modal */}
      <SubscriptionUpgradeModal
        isOpen={showUpgradeModal}
        onClose={() => setShowUpgradeModal(false)}
        feature="ai_features"
      />
    </div>
  )
}

export default AIAgentPanel 